import {
  Box,
  Button,
  Card,
  CardContent,
  FormControl,
  FormControlLabel,
  FormLabel,
  Grid,
  IconButton,
  MenuItem,
  Popover,
  Radio,
  RadioGroup,
  Select,
  Switch,
  Typography,
} from "@mui/material";
import SettingsIcon from "@mui/icons-material/Settings";
import SettingsReport from "./SettingsReport";
import StrategyTaxStateSetting from "./StrategyTaxStateSetting";
import StrategyFederalTaxSetting from "./StrategyFederalTaxSetting";
import {
  PerformanceReportTypeEnum,
  ReportValueCardProps,
  PDFReportRequest,
  PDFReportPerformanceChart,
  PDFReportClientInfoItem,
  PDFReportThemeInfoItem,
  ReportPdfResponse,
  OrionTransaction,
  BucketPairReportStatus,
  BucketReportStatusEnum,
  BucketTotals,
  BucketSingleInfoCards,
  BucketThemedCards,
  DiversifyThemedCardValues,
  HarvestStockCounts,
  KeepCardValues,
  ParticipationDirection,
  KeepThemedCardValues,
  ProtectThemedCardValues,
  ReportThemedCardConfig,
  PDFReportValueType,
  PDFReportChartType,
} from "helpers/types";
import ChartsReport from "./Charts/ChartsReport";
import { ChartSpec } from "./Charts/chartSeriesSelection";
import { buildOverlayChartData } from "./Charts/overlayChartData";
import {
  BucketSummaryEnum,
  BucketTwrFormula,
  ReportJobStatus,
  SingleValueCardColor,
  SingleValueCardType,
} from "constants/index";
import {
  StrategyReportSingleValueCardLabels,
  StrategyReportSingleValueCardDescriptions,
  StrategyReportThemedCardLabels,
  StrategyReportThemedCardDescriptions,
  StrategyReportThemedCardValuesLabels,
  StrategyReportThemedCardBlurbs,
  KEEP_TOTAL_OPTION,
  DIVERSIFY_ALL_STOCKS_OPTION,
} from "helpers/constants";
import { formatUsDate } from "helpers";
import { toPdfValues } from "./reportPdfUtils";
import SingleValueCard from "./SingleValueCard";
import MultiValuesCard from "./MultiValuesCard";
import CurrentAllocationChartReport, {
  ALLOCATION_SERIES_CONCENTRATION,
  ALLOCATION_SERIES_DIVERSIFIED,
  FULL_ALLOCATION_PCT,
  allocationSlices,
} from "./Charts/CurrentAllocationChartReport";
import { DrawdownComparisonChart } from "./Charts/DrawdownComparisonChart";
import {
  useSettingsReportValue,
  useStrategyReportValue,
} from "hooks/useReportStorage";
import { useMaxDropLimits } from "hooks/useMaxDropLimits";
import {
  SETTINGS_ACCOUNT_IDS_KEY,
  SETTINGS_CLIENT_ID_KEY,
} from "./ClientPicker";
import {
  BEGINNING_CONCENTRATION_DEFAULT,
  useBeginningConcentration,
} from "hooks/useBeginningConcentration";
import { useFederalTaxRate } from "hooks/useFederalTaxRate";
import { useStateTaxRate } from "hooks/useStateTaxRate";
import dayjs from "dayjs";
import { useMemo, useState, useCallback, useEffect } from "react";
import {
  useStartBucketReportJobMutation,
  useLazyGetBucketReportJobStatusQuery,
  useGetReportPdfMutation,
} from "features/trading/accounts";
import { BucketNumberEnum } from "features/trading/buckets";
import BucketSummarySelect from "components/buckets/BucketSummarySelect";
import { pollReportJob, reportJobErrorMessage } from "./reportJobPolling";
import {
  ReportSettingsProvider,
  useReportSettings,
} from "./ReportSettingsContext";
import TransactionModal from "./TransactionModal";
import RequestNotification from "components/ui/RequestNotification";
import { extractReportErrorMessage } from "./extractReportErrorMessage";
import { toFiniteNumber } from "components/ui/formatters";
import {
  NO_REPORT_DISPLAY,
  NOT_IMPLEMENTED_DISPLAY,
  SELECT_STOCK_DISPLAY,
  UNAVAILABLE_DISPLAY,
} from "./reportFieldStates";
import {
  AVAILABLE_MARGIN_BALANCE_DEFAULT,
  useAvailableMarginBalance,
} from "./useAvailableMarginBalance";
import {
  summaryIncomeTile,
  summaryMarginInterestTile,
  summaryTileDimmed,
  summaryTileValue,
  summaryValueCardFooters,
} from "./summaryCardFooters";
import { useUncalculatedReportValue } from "./useUncalculatedReportValue";

/**
 * What every field on this report shows before a figure has replaced it.
 *
 * These were invented numbers -- a $10,000,000 starting value, a 4.5%
 * time-weighted return, 95% participation, 12 holdings -- and they were the
 * report's most dangerous feature: plausible, precise, and indistinguishable
 * on screen from a calculated result. A reader had no way to tell a report
 * nobody had run from one that had, and the same figures reached the
 * client-facing PDF under the ordinary labels.
 *
 * So every one of them is now a seed that says it is not a figure, and WHICH
 * seed answers the question the reader asks next -- "why is there nothing
 * here, and what do I do about it?":
 *
 *   NO_REPORT       -- the backend calculates this one. It is replaced the
 *                      moment a report runs for the chosen account and period.
 *
 *   NOT_IMPLEMENTED -- no backend calculation produces this one yet. Its
 *                      setter sits commented out further down this file,
 *                      waiting for the bucket report to return the field, so
 *                      running a report will NOT fill it in. Saying "No
 *                      Report" here would send an operator to re-run a report
 *                      that cannot change it.
 *
 * Both remain editable: an operator can still type a figure over either, which
 * is how the unimplemented rows are filled in today.
 *
 * AND WHICH SEED A ROW CARRIES NOW DECIDES WHERE ITS FIGURE LIVES, because
 * the two answer different questions about who can replace it.
 *
 * A NO_REPORT row is localStorage-backed like the rest of the report: a run
 * replaces whatever is under it, so a stale figure has a way out.
 *
 * A NOT_IMPLEMENTED row has no such way out -- no run will ever overwrite it
 * -- and the report's store is one flat object with no client in its key, so
 * the figure an operator typed for one client was handed straight back under
 * the next client's name, in full figure weight, and printed on their PDF.
 * Those rows are held per REPORT instead (useUncalculatedReportValue, keyed
 * on reportScopeKey), which drops the figure when the client changes and
 * returns every row to its seed on a page load. Note what that hook is NOT:
 * plain component state, which this report cannot use -- a bare Tab press
 * unmounts the whole panel, and the hook's docstring carries the rest.
 *
 * AVAILABLE_MARGIN_BALANCE is the one exception to all of the above: it is
 * hard-coded to 0 at the UI level rather than seeded, on the operator's
 * instruction, and re-confirmed as such in task 3251. It shows $0.00, exports
 * $0.00, is not persisted, and is reset by that same effect --
 * useAvailableMarginBalance is the single home for all of that.
 *
 * The drawdown chart's y-axis bounds are NOT here at all: they are derived
 * from the protect asset's own two drops and stored per asset by
 * useMaxDropLimits, which is their single home. Beginning Concentration is
 * the one FIGURE that keeps a number, and its own note below says why that
 * is a definition rather than a seed.
 */
const STRATEGY_REPORT_DEFAULTS = {
  // Summary cards -- the backend's card_1..card_6.
  STARTING_VALUE: NO_REPORT_DISPLAY,
  ENDING_VALUE: NO_REPORT_DISPLAY,
  NET_CHANGE: NO_REPORT_DISPLAY,
  TIME_WEIGHTED_RETURN: NO_REPORT_DISPLAY,
  INCOME_RECEIVED: NO_REPORT_DISPLAY,
  MARGIN_INTEREST: NO_REPORT_DISPLAY,
  // Keep -- applied per picked asset by applyKeepShares.
  SHARES_START: NO_REPORT_DISPLAY,
  SHARES_END: NO_REPORT_DISPLAY,
  VALUE_START: NO_REPORT_DISPLAY,
  VALUE_END: NO_REPORT_DISPLAY,
  DIVIDENDS: NO_REPORT_DISPLAY,
  // A run DOES produce this rate now (task 3345): the Keep payload carries
  // it per asset, applyKeepShares applies it like every other row of the
  // card, and the seed says "No Report" for the same reason its seven
  // neighbours do -- nothing has run yet, so there is no figure and nothing
  // to explain. It read NOT IMPLEMENTED while the payload's
  // participation_rate was a reserved zero.
  PARTICIPATION_RATE: NO_REPORT_DISPLAY,
  BASIS: NO_REPORT_DISPLAY,
  TAX_SAVINGS: NO_REPORT_DISPLAY,
  // Protect -- max_stock_drop per stock, oea_drop and collar_pl from the card.
  MAX_STOCK_DROP: NO_REPORT_DISPLAY,
  OEA_DROP: NO_REPORT_DISPLAY,
  COLLAR_PL: NO_REPORT_DISPLAY,
  // Hand-entered, not seeded, and its default is not a figure at all: the row
  // reads N/A until an operator types into it, which is why the entry here is
  // null rather than a number (operator, task 3724). Listed so this table
  // stays a complete census of the rows' defaults, but POINTING at
  // useAvailableMarginBalance, which is what the row actually renders and
  // where the whole of why lives.
  AVAILABLE_MARGIN_BALANCE: AVAILABLE_MARGIN_BALANCE_DEFAULT,
  // Diversify -- TWO of its five rows are calculated: Overall Diversified
  // Performance (Bucket 2 and Bucket 3's aggregate weight-adjusted return,
  // task 3346, narrowed from all three by task 3573) and Current
  // Concentration (task 3348). A run DOES fill both in, so both
  // seed "No Report". The other two carry no calculation at all, and saying
  // "No Report" for them would send an operator to re-run a report that
  // cannot change them. Beginning Concentration is neither -- it is a
  // definition, seeded from its own hook below.
  OVERALL_DIVERSIFIED_PERFORMANCE: NO_REPORT_DISPLAY,
  TAX_LOSS_HARVEST_SLEEVE: NO_REPORT_DISPLAY,
  // The one row on these cards seeded with a real NUMBER rather than a
  // "no figure" state, and the one place that is not the invented-seed
  // failure reportFieldStates describes: 100% is not an estimate of this
  // account's opening concentration, it is the DEFINITION of the baseline
  // the Diversify card measures against -- the client walked in holding
  // nothing but the concentrated stock. It stays editable for the account
  // that did not (operator, task 3141).
  //
  // Listed here so this table stays a complete census of the rows' seeds, but
  // POINTING at the hook that owns it rather than restating the number -- the
  // hook is where the seed and its lifetime are decided together.
  BEGINNING_CONCENTRATION: BEGINNING_CONCENTRATION_DEFAULT,
  // NO_REPORT and not NOT_IMPLEMENTED, and the difference is the whole of
  // reportFieldStates' taxonomy: the bucket report DOES produce this figure
  // now (build_diversify_card), so "run one" is true of the empty row --
  // where NOT IMPLEMENTED would tell the operator to stop trying.
  CURRENT_CONCENTRATION: NO_REPORT_DISPLAY,
  // Same move as Current Concentration above, and for the same reason: the
  // bucket report produces this one too now (resolve_holdings_count, task
  // 3349), so "run one" is true of the empty row.
  HOLDINGS: NO_REPORT_DISPLAY,
  // Harvest -- no row of it is calculated either.
  REALIZED_LOSSES: NOT_IMPLEMENTED_DISPLAY,
  LOSSES_USED: NOT_IMPLEMENTED_DISPLAY,
  POOL_REMAINING: NOT_IMPLEMENTED_DISPLAY,
  // Calculated -- `_resolve_unrealized_gl` is the one figure of Harvest the
  // bucket report produces today, so a run DOES fill this in and the seed has
  // to say so. It read NOT_IMPLEMENTED, which sent an operator away from the
  // one Harvest row re-running would have answered.
  UNREALIZED_GAINS: NO_REPORT_DISPLAY,
  // Calculated too, and PER BUCKET 3 STOCK: the run carries a figure for
  // every stock in the bucket, so the seed says "run a report" rather than
  // "nothing computes this". Once a report HAS run, the rows show
  // SELECT_STOCK_DISPLAY until the reader picks one -- a third question,
  // which reportFieldStates carries.
  SHARES_SOLD: NO_REPORT_DISPLAY,
  SELLABLE_TODAY: NO_REPORT_DISPLAY,
  // Axis bounds, not figures -- see above.
};

/**
 * What the Keep card's figures ARE right now.
 *
 * ONE state for the CARD rather than a flag per row, because every field of
 * that card is Bucket 1's by definition (the backend's _build_themed_cards
 * says so): they are produced together or not at all, and a run either
 * carried the bucket or it did not.
 *
 *   "unrun"   -- no completed run has produced a Keep card in this session.
 *                Every row shows its NO_REPORT seed, and whatever
 *                localStorage holds for it WAITS UNDERNEATH rather than being
 *                shown. That store is one flat object with no client,
 *                account, period or ticker anywhere in its key
 *                (useReportStorage), so a figure read back out of it belongs
 *                to whichever report was last on screen -- another period's,
 *                another ticker's, or another client's.
 *   "absent"  -- a run COMPLETED and carried no Bucket 1 data at all. That is
 *                an answer rather than a silence, so the rows say so:
 *                KEEP_NO_BUCKET_1 behind an N/A.
 *   "figures" -- a run produced the card. The rows carry the picked option's
 *                values.
 *
 * The two non-"figures" states are what task 3155 is: a run that carried no
 * Bucket 1 data left the whole card showing the PREVIOUS run's figures --
 * 60,000 shares, a $2,052,600 opening value, a 472.39% participation rate --
 * with no header picker beside them, because the picker only renders when
 * there is something to pick. Every one of those read as this run's result.
 */
export type KeepFiguresState = "unrun" | "absent" | "figures";

/**
 * The Keep card's own "no figure" sentence, and the one on these cards the
 * FRONTEND authors.
 *
 * Every other one is the backend's, single-homed in bucket_totals.py, because
 * every other one explains a figure the backend reached and refused. This one
 * explains a card the response never carried at all, which is a fact only the
 * reader of that response holds.
 *
 * It names the STOCK rather than the calculation because that is the question
 * the reader is actually asking. The card's header picker is absent in this
 * state -- there is nothing to pick -- so what they see is a figure with no
 * stock beside it and no way to find out which stock it is for, and asking
 * that is what sent this here (operator, task 3155).
 */
export const KEEP_NO_BUCKET_1 =
  "No figure — this run produced no Bucket 1 results, so there is no stock " +
  "for the Keep card to report on.";

/**
 * The Keep card's rows, named so that one of them can be marked as the
 * reader's own. Keys rather than labels: a label is display text, and
 * rewording one must not silently detach a row from its edit.
 */
export type KeepRowKey =
  | "shares_start"
  | "shares_end"
  | "value_start"
  | "value_end"
  | "dividends"
  | "participation_rate"
  | "basis"
  | "tax_savings";

const NO_KEEP_ROWS_EDITED: ReadonlySet<KeepRowKey> = new Set<KeepRowKey>();

/**
 * What ONE Keep row shows, from the card's state and whether the reader has
 * taken that row over. Every row of the card reads through here, so the three
 * states are decided once rather than eight times.
 *
 * `stored` is the row's own persisted figure and `reason` the sentence the
 * backend sent about it. BOTH ARE READ ONLY IN THE "figures" STATE, and that
 * is the whole point of this function: in the other two the stored figure is
 * not this run's result, so showing it would credit it to a run that never
 * produced it, and a backend sentence about it would explain a number this
 * run never reached.
 *
 * An EDITED row outranks every state, because from the moment the reader
 * types a figure it is theirs: the row shows what they typed and carries no
 * reason, since an explanation of why the backend had none is describing a
 * number nobody is looking at any more. It is per ROW and never card-wide --
 * one shared flag would take the other seven rows out of their seed the
 * moment one was typed into, revealing seven stored figures nobody entered
 * this session as if this run had produced them.
 */
export const keepRowFigure = (
  state: KeepFiguresState,
  edited: boolean,
  stored: number | string,
  reason?: string,
): Pick<ReportValueCardProps, "value" | "unavailableReason"> => {
  if (edited) return { value: stored };
  if (state === "figures") return { value: stored, unavailableReason: reason };
  // The empty sentinel WITH a reason is what MultiValuesCard renders as a
  // muted "N/A" behind the info icon, and what toPdfValues drops from the
  // client-facing export. Either half alone would be wrong here: the reason
  // without the sentinel would leave a figure underneath a row that reads
  // N/A, and the sentinel without the reason is the silent gap this state
  // exists to stop being.
  if (state === "absent") return { value: "", unavailableReason: KEEP_NO_BUCKET_1 };
  // No reason, on purpose. "No Report" is not a refusal -- nothing has run,
  // so there is nothing to explain -- and reportFieldStates keeps the two
  // apart deliberately: an N/A asserts the backend reached this figure and
  // could not produce it, which of a run nobody has made yet is false.
  return { value: NO_REPORT_DISPLAY };
};

/**
 * The Keep card's Participation Rate for ONE option of the card -- one stock,
 * or the Total -- as the stored figure, the sentence that qualifies it, and
 * the direction the rate measures (which labels the row:
 * keepParticipationLabel). `keepRowFigure` above takes it from here and decides what the row does
 * with it.
 *
 * This is `timeWeightedReturnFigure`'s job for `card_4` done for the Keep
 * card's one refusable row, and the two hold the same contract because the
 * backend sends the same shape: `participation_rate` is null EXACTLY when
 * `participation_rate_unavailable` carries the reason (types.ts).
 *
 * The null becomes the EMPTY SENTINEL rather than travelling on, for the
 * three reasons that made card_4 do the same: the value is what gets STORED,
 * what the PDF prints and what survives the report's remount, so passing a
 * null through would leave the PREVIOUS option's rate underneath a row
 * reading N/A -- and on this card that is a rate credited to the wrong
 * ticker, which is the Keep card's own reported defect (task 3155).
 *
 * `?? ""` and not `|| ""`, on BOTH halves: a measured 0.00% is a result -- a
 * strategy that captured none of its stock's rise -- and a falsy test would
 * turn that result into a refusal. It is also what makes a payload from a job
 * that completed before this field shipped read exactly as it did before:
 * such a payload carries `participation_rate: 0` and no reason at all, so it
 * yields that 0 and no sentence.
 */
export const keepParticipationFigure = (
  values: Pick<
    KeepCardValues,
    | "participation_rate"
    | "participation_rate_unavailable"
    | "participation_direction"
  >,
): {
  rate: number | string;
  reason?: string;
  direction?: ParticipationDirection;
} => ({
  rate: values.participation_rate ?? "",
  reason: values.participation_rate_unavailable ?? undefined,
  direction: values.participation_direction ?? undefined,
});

/**
 * The Keep card participation row's label for the direction its rate
 * measures (task 3956): "Downside Participation Rate" when the stock and its
 * options both fell, and "Upside Participation Rate" otherwise -- both rose,
 * or no rate at all (a refusal, the Total, or no run yet), which the operator
 * chose to keep under the Upside label with its N/A and reason.
 */
export const keepParticipationLabel = (
  direction?: ParticipationDirection,
): StrategyReportThemedCardValuesLabels =>
  direction === "downside"
    ? StrategyReportThemedCardValuesLabels.DOWNSIDE_PARTICIPATION_RATE
    : StrategyReportThemedCardValuesLabels.UPSIDE_PARTICIPATION_RATE;

/**
 * What the Diversify card's Current Concentration shows, in the two places
 * that show it: the ROW, and the allocation donut beside it.
 *
 * ONE function for both, and that is the whole reason it exists. The row and
 * the donut have to agree about whether this run produced a figure at all,
 * and the rule expressed twice is how a row reading N/A ends up next to a
 * donut still drawing a complete ring from the PREVIOUS run's stored
 * concentration -- two answers to one question, on one card. `keepRowFigure`
 * above is the same pattern for the Keep card's eight rows.
 *
 * `stored` is the persisted figure (or, before any run, the "No Report"
 * seed); `reason` is the sentence the backend sent when it reached the figure
 * and refused it (build_diversify_card holds the three it can send).
 *
 * A refusal returns the EMPTY SENTINEL with the reason -- which is what
 * MultiValuesCard renders as a muted "N/A" behind the info icon and what
 * toPdfValues drops from the client-facing export -- and `pct: null`, which
 * is what stops both donuts drawing. The stored figure is deliberately left
 * alone underneath: a refusal must not destroy a hand-entered number, it
 * must only stop it passing for this run's answer.
 */
export const currentConcentrationFigure = (
  stored: number | string,
  reason?: string,
): Pick<ReportValueCardProps, "value" | "unavailableReason"> & {
  pct: number | null;
} =>
  reason
    ? { value: "", unavailableReason: reason, pct: null }
    : { value: stored, pct: toFiniteNumber(stored) };

/**
 * The four fields the Diversify card's stock dropdown reads, lifted out of
 * the run's payload so the resolution below can be tested without one.
 */
export type DiversifyStockConcentrations = Pick<
  DiversifyThemedCardValues,
  | "current_concentration_by_symbol"
  | "current_concentration_by_symbol_unavailable"
  | "beginning_concentration_by_symbol"
  | "beginning_concentration_by_symbol_unavailable"
>;

/**
 * What the Diversify card's two concentration rows show for ONE selected
 * Bucket 1 stock (operator, task 3508) -- the figure, or the empty
 * sentinel behind the sentence saying why this run has none.
 *
 * ONE function for both rows, and the same pair every refused figure on
 * these cards renders: a muted "N/A" behind an info icon rather than a
 * number nobody calculated. The two rows refuse on their OWN terms and so
 * read their own reason key -- a run can price the whole portfolio today
 * and still have opened Bucket 1 at nothing, which blanks the opening row
 * while today's share stays perfectly measurable.
 *
 * A SYMBOL THE PAYLOAD DOES NOT CARRY is the third state and gets the
 * empty sentinel with no sentence: the map is keyed by the stocks this run
 * produced, so a symbol missing from it was never measured. It is
 * unreachable while the dropdown is built from the same run's stocks, and
 * it is here so that a payload and a picker that disagree cannot put the
 * PREVIOUS selection's number under this one's name.
 *
 * Note what this never returns: the All Stocks selection. That row is the
 * reader's own 100% baseline (useBeginningConcentration) and the card's
 * stored whole-of-Bucket-1 concentration, neither of which is this
 * payload's to serve -- the caller keeps those two on their own path.
 */
export const diversifyStockFigure = (
  bySymbol: Record<string, number> | null,
  reason: string | null,
  symbol: string,
): Pick<ReportValueCardProps, "value" | "unavailableReason"> => {
  if (reason) return { value: "", unavailableReason: reason };
  const figure = bySymbol?.[symbol];
  // `== null` and not `!`, so a genuine 0% -- a client who has left this
  // stock entirely -- is a result rather than a blank.
  return figure == null ? { value: "" } : { value: figure };
};

/**
 * A concentration row's label with the stock it was measured for appended
 * (operator, task 3508): "Current Concentration: AAPL".
 *
 * The All Stocks selection keeps the plain label, because that IS what the
 * row has always meant -- a suffix there would suggest a narrowing that
 * has not happened.
 */
export const concentrationRowLabel = (label: string, symbol: string): string =>
  symbol === DIVERSIFY_ALL_STOCKS_OPTION ? label : `${label}: ${symbol}`;

/**
 * What the Time-Weighted Return card shows for one scope, from that scope's
 * cards payload.
 *
 * `card_4` is null EXACTLY when `card_4_unavailable` carries a reason
 * (types.ts), and this is the one place that reading is turned into the two
 * props the card takes. A null becomes the EMPTY SENTINEL rather than being
 * passed through: the card reads N/A from the reason either way, but the
 * value is also what gets STORED, what the export prints and what survives
 * the report's remount -- so leaving the last run's number behind would
 * credit this scope with a return it never reported, on all three.
 *
 * A scope that produced a figure carries no reason, which is what keeps the
 * fix confined to the scopes that had none: a measured 0.00% is a result and
 * still reads as one.
 *
 * `card_4_unavailable` is absent on a job that COMPLETED before the field
 * shipped and is served from cache; such a payload carries `card_4: 0` and
 * reads exactly as it did before, which is the graceful half of typing it
 * optional rather than required.
 *
 * `formula` is the gear's pick, and the figure it names is read off
 * `card_4_by_formula` (operator, task 3764) -- which is what lets the pick
 * change the card with NO re-run, because every formula's answer arrived
 * with the run. Falling back to `card_4` covers the two payloads that carry
 * no such map: a job cached before it shipped, and any caller that does not
 * care which formula it is looking at. Both then read exactly as they did
 * before, showing the RUN's figure whichever way the gear is set.
 *
 * The fallback is on the ENTRY, not on the map: a scope present in the map
 * still refuses per formula, and `{value: null, unavailable: "..."}` is a
 * refusal to render as N/A rather than a miss to paper over with `card_4`.
 */
export const timeWeightedReturnFigure = (
  cards: Pick<
    BucketSingleInfoCards,
    "card_4" | "card_4_unavailable" | "card_4_by_formula"
  >,
  formula?: BucketTwrFormula,
  // NOT Pick<ReportValueCardProps, ...>: that type's `value` admits null so a
  // card can render a null arriving from a cached payload, and borrowing it
  // here published a nullable `value` this function cannot actually return --
  // the `?? ""` below has already turned the payload's null into the empty
  // sentinel. The stored figure it feeds is `number | string`, so the wider
  // borrowed type failed to compile against its own setter. Stating the
  // narrower truth keeps it assignable to the card props either way.
): { value: number | string; unavailableReason?: string } => {
  const picked =
    formula === undefined ? undefined : cards.card_4_by_formula?.[formula];
  if (picked) {
    return {
      value: picked.value ?? "",
      unavailableReason: picked.unavailable ?? undefined,
    };
  }
  return {
    value: cards.card_4 ?? "",
    unavailableReason: cards.card_4_unavailable ?? undefined,
  };
};

/**
 * What the Starting and Ending Value tiles say about WHERE their figure came
 * from, when the backend says the bucket did not cover the whole period
 * (operator, task 3730).
 *
 * A note appears only where `card_1_date`/`card_2_date` arrived, and types.ts
 * states when the backend sends them -- the reader's question is "why is this
 * figure not my period's", so a date repeated on every run that priced a
 * Monday open would be noise on top of the answer.
 *
 * Both dates absent is the ordinary case AND the shape a job cached before
 * they shipped carries, and the two want the same thing: no icon, no
 * tooltip, the figure alone. So `undefined` is returned per tile rather than
 * a sentence saying the date matches, and the caller sets BOTH tiles on
 * every scope change, which is what clears a previous scope's note.
 */
export const summaryValueDateNotes = (
  cards:
    | Pick<BucketSingleInfoCards, "card_1_date" | "card_2_date">
    | undefined,
): { startingValue?: string; endingValue?: string } => ({
  startingValue: cards?.card_1_date
    ? `As of ${formatUsDate(cards.card_1_date)}`
    : undefined,
  endingValue: cards?.card_2_date
    ? `As of ${formatUsDate(cards.card_2_date)}`
    : undefined,
});

/**
 * What the Long Value scope's three value cards say when the payload carried
 * no `long_value` entry to read them off (operator, task 3636).
 *
 * The shape that reaches: a job CACHED before the entry shipped, served to
 * this frontend for the rest of its TTL because the cache key is versioned
 * on the data and never on the code. The operator chose N/A over the old
 * sum-of-the-bucket-cards fallback, and the reason is the point of the task:
 * that sum is the wrong figure this change exists to stop showing, so
 * falling back to it would keep serving it to every cached job rather than
 * saying plainly that the report predates the answer.
 *
 * User-facing prose, shown on the card's info icon, so it names no function
 * and no module -- and says what to DO about it, since re-running the report
 * is what actually produces the figure.
 */
export const LONG_VALUE_VALUES_UNAVAILABLE =
  "This report was calculated before its opening and closing values could " +
  "be measured across all three buckets. Re-run the report to see them.";

/**
 * The Long Value scope's six figures as this page builds them.
 *
 * `BucketSingleInfoCards` with the three value cards widened to carry the
 * EMPTY SENTINEL, for the same reason timeWeightedReturnFigure narrows its
 * own return rather than borrowing the card's props: the figure here is what
 * gets stored, exported and survives a remount, and `""` is how this page
 * has always said "no figure" to all three. `values_unavailable` is set
 * EXACTLY when those three are the sentinel -- the same invariant card_4 and
 * card_4_unavailable hold, and the caller relies on it to decide whether to
 * put a reason on the cards at all.
 */
export interface LongValueSummaryCards
  extends Omit<BucketSingleInfoCards, "card_1" | "card_2" | "card_3"> {
  card_1: number | string;
  card_2: number | string;
  card_3: number | string;
  values_unavailable?: string;
}

/** The three numbered scopes the drop-down offers, in its own order. */
const NUMBERED_BUCKET_SUMMARIES = [
  BucketSummaryEnum.BUCKET_1,
  BucketSummaryEnum.BUCKET_2,
  BucketSummaryEnum.BUCKET_3,
] as const;

/**
 * Two decimal places, the precision every card the backend sends already
 * carries. Summing three of them in binary floating point reintroduces the
 * drift they were rounded to remove (0.1 + 0.2), and a currency card is the
 * one place a trailing 0.000000000000004 is read as a real cent.
 */
const roundCurrency = (value: number): number => Math.round(value * 100) / 100;

/**
 * The Long Value scope's six summary cards, summed from the BUCKETS' own
 * cards rather than read from the backend's `long_value` entry.
 *
 * The response carries that entry and this deliberately does not use it: its
 * six figures answer different questions from the ones the Summary drop-down
 * is being asked here (operator, task 3542), and each difference is a choice
 * rather than a discrepancy to reconcile.
 *
 *   - **The three value cards are the BACKEND entry's** (operator, task
 *     3636), which is where they came from before task 3542 and where they
 *     are again. Summing the buckets' own cards looked like it made the
 *     screen add up, and could not: each bucket's card_1 is ITS OWN first
 *     priced day, so the total described a portfolio as of three different
 *     dates, and a bucket that started late contributed its inception value
 *     as though the period had opened at it. The entry sources all three
 *     figures as of ONE date -- the period's requested start -- counting a
 *     bucket that did not exist yet as the 0 it was worth, and carrying a
 *     bucket that existed but has no row that day (the pair hold trading
 *     days, so a weekend or holiday start has none) at what it was worth
 *     going in. So the card agrees with the first point of the Long Value
 *     chart line again, which the sum could not on a ragged start.
 *   - **Income is the BACKEND entry's, and it is the one figure here that is
 *     not summed from the cards** (operator, task 3574). The Long Value
 *     income is every bucket's dividends plus the option cashflow of Bucket
 *     2 ALONE: Bucket 1's premium is the Protect card's Collar P&L, and
 *     income that counted it would report one cashflow twice under two
 *     names. That formula cannot be built from the bucket cards, which carry
 *     each bucket's dividends and premium already added together, so this
 *     reads the figure the backend resolved (`combined_scope_income`) rather
 *     than re-deriving a number it does not have the terms for. It was a
 *     straight sum of the three card_5s until 3574, which is why the three
 *     income cards on screen no longer add up to this one -- the workbook's
 *     Long Value section states the difference as its own row.
 *   - **card_4 is the portfolio's aggregate return** -- each bucket's return
 *     weighted by its share of the opening value -- where the backend entry
 *     refuses the figure outright (COMBINED_SCOPE_NO_WEIGHTED_RETURN) on the
 *     grounds that a sum across buckets has no per-bucket weighting of its
 *     own. That aggregate is exactly the weighting it was missing, so the
 *     card is filled in from the Diversify payload's
 *     `portfolio_aggregate_return` -- the WHOLE portfolio's figure, which is
 *     no longer the Diversify card's Overall Diversified Performance row:
 *     task 3573 narrowed that row to Buckets 2 and 3, and Long Value is all
 *     three summed.
 *
 * `Net Value`, the sibling combined scope, is deliberately NOT changed and
 * still reads the backend's entry throughout: task 3542 was scoped to Long
 * Value alone, so the two combined scopes answer on different terms and only
 * this one says so. INCOME IS THE ONE FIGURE THEY STILL SHARE, and as of task
 * 3574 they share it because this scope came back to the entry rather than
 * because that one left it.
 *
 * **Margin is charged to an ACCOUNT rather than to a bucket**, and Buckets 1
 * and 2 reference the SAME account -- so their card_6 is one charge reported
 * twice, and adding all three would bill the portfolio for it twice over.
 * The pair contributes once and Bucket 3's own account joins it: $500, $500
 * and $700 on the three cards is $1,200 here, not $1,700.
 *
 * **Its backend twin is `long_value_breakdown` in `bucket_totals.py`**, which
 * writes the Long Value section of the Excel workbook's `Long & Net Value`
 * sheet from the same cards on the same rules (task 3546). The section exists
 * to show the working behind THESE cards, so the two are one arithmetic
 * maintained in two languages: a change to the pair-margin rule, to which
 * buckets are summed, or to which card each figure comes off has to be made
 * in both, or the sheet starts explaining a figure the page no longer shows.
 *
 * Returns null when the run produced no numbered bucket at all, which the
 * caller reads as "nothing to apply" -- the same guard an absent scope gets,
 * so the cards keep whatever they held rather than dropping to zeroes no
 * calculation produced.
 */
export const longValueCards = (
  totals: BucketTotals,
  aggregateReturn: { value: number | null; unavailable?: string },
): LongValueSummaryCards | null => {
  const present = NUMBERED_BUCKET_SUMMARIES.map(
    (summary) => totals[summary]?.single_info_cards,
  ).filter((cards): cards is BucketSingleInfoCards => cards !== undefined);
  if (!present.length) return null;

  const sumOf = (read: (cards: BucketSingleInfoCards) => number): number =>
    roundCurrency(present.reduce((total, cards) => total + read(cards), 0));

  // The three VALUE cards are the backend entry's again (operator, task
  // 3636) -- the one figure on this scope a sum of the bucket cards could
  // not state correctly. Each bucket's card_1 is its OWN first priced day,
  // so adding the three totals a portfolio across three different dates,
  // and a bucket that started late contributed its inception value as
  // though the period had opened at it. The entry opens the period on ONE
  // date for all three, counting a bucket that did not exist yet as the 0
  // it was worth (bucket_totals.build_long_daily_values).
  //
  // CHAINED PAST `single_info_cards` for the same reason card_5 below is:
  // the backend builds it from a series it can refuse, so null is a shape
  // the runtime produces and the type does not describe.
  const entry = totals[BucketSummaryEnum.LONG_VALUE]?.single_info_cards;

  // The pair's single charge, taken from whichever of the two the run
  // produced -- they carry the same figure because they name the same
  // account, so either answers, and `??` rather than `||` because a period
  // that accrued no margin at all is a real 0 and not a missing bucket.
  const pairMargin =
    totals[BucketSummaryEnum.BUCKET_1]?.single_info_cards.card_6 ??
    totals[BucketSummaryEnum.BUCKET_2]?.single_info_cards.card_6 ??
    0;
  const bucket3Margin =
    totals[BucketSummaryEnum.BUCKET_3]?.single_info_cards.card_6 ?? 0;

  // card_4 is null EXACTLY when card_4_unavailable carries a reason -- the
  // invariant build_info_cards RAISES on backend-side. Here the refusal
  // simply wins: this component renders the whole report, so throwing over a
  // malformed pair would blank the page instead of one card, and a card
  // showing N/A with the reason beside it is the honest reading of a payload
  // that sent both.
  const refusal = aggregateReturn.unavailable;
  return {
    // The EMPTY SENTINEL rather than a stale number when the payload carried
    // no entry, on exactly the reasoning timeWeightedReturnFigure states for
    // card_4: the value is what gets stored, exported and survives a
    // remount, so leaving the previous run's figure behind would credit this
    // scope with an opening balance it never reported. The operator chose
    // N/A over the old sum here (task 3636): a sum is the figure this task
    // exists to stop showing, so falling back to it would keep serving the
    // wrong answer to every cached job for the rest of its TTL.
    card_1: entry?.card_1 ?? "",
    card_2: entry?.card_2 ?? "",
    card_3: entry?.card_3 ?? "",
    values_unavailable: entry ? undefined : LONG_VALUE_VALUES_UNAVAILABLE,
    card_4: refusal ? null : aggregateReturn.value,
    card_4_unavailable: refusal ?? null,
    // The backend's own figure, not a sum of the cards -- the note above
    // says why this one scope reads the entry. `??` rather than `||` so a
    // period that genuinely earned no income is reported as the 0 it is;
    // the sum is the fallback for a response that carried no `long_value`
    // entry at all, which is the shape this component guarded before the
    // entry became load-bearing here.
    //
    // CHAINED PAST `single_info_cards` TOO, although the type declares it
    // required: the backend builds that object from a series it can refuse
    // (_scope_cards returns None on a scope with no priced day), so `null`
    // there is a shape the runtime can produce and the type does not
    // describe. A TypeError in this function blanks the whole report page,
    // which is a far worse answer than one card falling back to the sum.
    card_5:
      totals[BucketSummaryEnum.LONG_VALUE]?.single_info_cards?.card_5 ??
      sumOf((cards) => cards.card_5),
    card_6: roundCurrency(pairMargin + bucket3Margin),
  };
};

/**
 * What a single-value figure exports as, given the sentence (if any) saying
 * the backend refused it.
 *
 * The screen and the paper have to agree, and they take different routes to
 * the same field: the card reads N/A off the reason, while the export takes
 * the stored figure. Without this substitution a client's PDF would print a
 * return the backend declined to stand behind -- on the one document that
 * leaves the building (operator, task 3239).
 *
 * The two characters travel and the sentence does not: a tooltip has no paper
 * equivalent, and "N/A" is an accepted export value for exactly this reason
 * (services/constants.py PDF_REPORT_PLACEHOLDER_VALUES, kept in step by hand
 * across the language boundary).
 */
export const pdfFigureValue = (
  stored: number | string,
  unavailableReason?: string,
): string => (unavailableReason ? UNAVAILABLE_DISPLAY : String(stored));

// The Protect card's per-asset max-drop dictionary from the report response.
type ProtectDrops = ProtectThemedCardValues["max_stock_drop"];

// "Sellable Today" on paper with no date is the strongest false claim this
// card can make: the count is the account's latest position snapshot, which
// can be days old, and the word "Today" invites the reader to read it as
// this morning's. The caption beside the figure says the date on screen;
// the PDF has no room for a caption, so the export folds it into the label
// the way the OEA Max Drop's bucket list is folded into its own.
const SELLABLE_TODAY_PDF_LABEL = "Sellable Today";

/**
 * The day the Sellable Today count was actually taken on.
 *
 * Always shown when there is a backend count, and shown with no
 * drift/coverage test of its own: this date is the account's latest position
 * snapshot and has nothing to do with the period the reader chose, so there
 * is no run in which repeating it is noise -- and the row's own label says
 * "Today", which is the one thing the date may need to contradict.
 */
export const sellableAsOfCaption = (asOf: string | null): string | null =>
  asOf ? `as of ${dayjs(asOf).format("MM/DD/YYYY")}` : null;

// The two time-weighted return formulas the Summary gear offers. Neither
// names a date since task 3603 retired the one that did, so the label is a
// straight lookup; `startDate` stays in the signature because the caller has
// it and a date-bearing formula is the kind of thing that comes back.
const TWR_FORMULA_LABELS: Record<BucketTwrFormula, string> = {
  [BucketTwrFormula.DAILY_WEIGHT_ADJUSTMENT]: "Daily Weight Adjustment",
  [BucketTwrFormula.BUCKET_MARKET_VALUE]: "Bucket Market Value",
};

export const twrFormulaLabel = (
  formula: BucketTwrFormula,
  _startDate: string | undefined,
): string => TWR_FORMULA_LABELS[formula];

const DRAWDOWN_CHART_TITLE = "Drawdown Comparison";
const ALLOCATION_CHART_TITLE = "Current Allocation";
const CHART_SERIES_STOCK = "Stock";
const CHART_SERIES_OEA = "OEA";

function StrategyReportContent() {
  // SingleValueCard hooks
  const [startingValue, setStartingValue] = useStrategyReportValue(
    "starting_value_strategy",
    STRATEGY_REPORT_DEFAULTS.STARTING_VALUE,
  );
  const [endingValue, setEndingValue] = useStrategyReportValue(
    "ending_value_strategy",
    STRATEGY_REPORT_DEFAULTS.ENDING_VALUE,
  );
  const [netChange, setNetChange] = useStrategyReportValue(
    "net_change_strategy",
    STRATEGY_REPORT_DEFAULTS.NET_CHANGE,
  );
  const [timeWeightedReturn, setTimeWeightedReturn] = useStrategyReportValue(
    "time_weighted_return_strategy",
    STRATEGY_REPORT_DEFAULTS.TIME_WEIGHTED_RETURN,
  );
  const [incomeReceived, setIncomeReceived] = useStrategyReportValue(
    "income_received_strategy",
    STRATEGY_REPORT_DEFAULTS.INCOME_RECEIVED,
  );
  const [marginInterest, setMarginInterest] = useStrategyReportValue(
    "margin_interest_strategy",
    STRATEGY_REPORT_DEFAULTS.MARGIN_INTEREST,
  );

  // MultiValuesCard hooks - Keep
  const [sharesStart, setSharesStart] = useStrategyReportValue(
    "shares_start_strategy",
    STRATEGY_REPORT_DEFAULTS.SHARES_START,
  );
  const [sharesEnd, setSharesEnd] = useStrategyReportValue(
    "shares_end_strategy",
    STRATEGY_REPORT_DEFAULTS.SHARES_END,
  );
  const [valueStart, setValueStart] = useStrategyReportValue(
    "value_start_strategy",
    STRATEGY_REPORT_DEFAULTS.VALUE_START,
  );
  const [valueEnd, setValueEnd] = useStrategyReportValue(
    "value_end_strategy",
    STRATEGY_REPORT_DEFAULTS.VALUE_END,
  );
  const [dividends, setDividends] = useStrategyReportValue(
    "dividends_strategy",
    STRATEGY_REPORT_DEFAULTS.DIVIDENDS,
  );
  // Which client's card this is, read from the SETTINGS STORE the picker
  // writes rather than from `settingsInfo` -- the settings context lives
  // inside the same unmounting tab panel and starts empty on every remount,
  // so keying on it would read as an account change on every tab switch. The
  // hook's docstring carries the rest of that reasoning.
  const [storedClientId] = useSettingsReportValue(
    `${SETTINGS_CLIENT_ID_KEY}_strategy`,
    "",
  );
  const [storedAccountIds] = useSettingsReportValue(
    `${SETTINGS_ACCOUNT_IDS_KEY}_strategy`,
    "[]",
  );

  // WHICH REPORT the uncalculated rows' typed figures belong to, stated once
  // because two hooks now key on it: Beginning Concentration's own account
  // scoping (task 3141) and every row of Diversify/Harvest that no
  // calculation reaches. A figure typed against one client is not a claim
  // about the next, and this is what drops it when the client changes.
  const reportScopeKey = `${storedClientId}:${storedAccountIds}`;

  // A CALCULATED row as of task 3345, so it stores the way its seven
  // neighbours on this card do rather than through useUncalculatedReportValue.
  // That hook's own docstring draws the line: its report-scoped holder exists
  // for a row NO run can overwrite, where a figure typed for client A would
  // otherwise still be on screen under client B's name. A row a run replaces
  // is survivable in the flat store -- the next run overwrites it, and until
  // then keepRowFigure's card state is what stops the previous run's number
  // reading as this one's. Its old `upside_participation_strategy` key is
  // retired with it; whatever a browser holds under it is never read again.
  const [participationRate, setParticipationRate] = useStrategyReportValue(
    "participation_rate_strategy",
    STRATEGY_REPORT_DEFAULTS.PARTICIPATION_RATE,
  );
  // Why the rate has none, when it has none -- the picked option's own
  // sentence, in component state rather than storage on exactly the terms
  // unrealizedGainsUnavailable is: the reason belongs to the RUN while the
  // figure it explains is persisted, so a reload must not resurrect a
  // refusal from a run that is gone. Set only in applyKeepShares, beside the
  // figure it qualifies, so the pair can never come from two different
  // options.
  const [participationUnavailable, setParticipationUnavailable] = useState<
    string | undefined
  >(undefined);
  // Which way the rate measures, and so which label the row wears -- held
  // beside the reason on the same terms and set in the same place, so the
  // label always belongs to the option whose figure is on the row.
  const [participationDirection, setParticipationDirection] = useState<
    ParticipationDirection | undefined
  >(undefined);
  const [basis, setBasis] = useStrategyReportValue(
    "basis_strategy",
    STRATEGY_REPORT_DEFAULTS.BASIS,
  );
  const [taxSavings, setTaxSavings] = useStrategyReportValue(
    "tax_savings_strategy",
    STRATEGY_REPORT_DEFAULTS.TAX_SAVINGS,
  );

  // MultiValuesCard hooks - Protect
  const [maxStockDrop, setMaxStockDrop] = useStrategyReportValue(
    "max_stock_drop_strategy",
    STRATEGY_REPORT_DEFAULTS.MAX_STOCK_DROP,
  );
  const [oeaDrop, setOeaDrop] = useStrategyReportValue(
    "oea_drop_strategy",
    STRATEGY_REPORT_DEFAULTS.OEA_DROP,
  );
  const [collarPL, setCollarPL] = useStrategyReportValue(
    "collar_pl_strategy",
    STRATEGY_REPORT_DEFAULTS.COLLAR_PL,
  );
  // Available Margin Balance: the one field on this report that is neither
  // seeded nor persisted, and the one whose default is a STATE rather than a
  // figure -- N/A until somebody types into it. Its whole state machine, and
  // why, lives in useAvailableMarginBalance.
  const {
    row: availableMarginBalanceRow,
    onValueChange: handleAvailableMarginBalanceChange,
    reset: resetAvailableMarginBalance,
  } = useAvailableMarginBalance();

  // MultiValuesCard hooks - Diversify
  //
  // Split for the reason the Harvest card below is split. Every
  // UNCALCULATED row here is session-only, for the reason
  // useUncalculatedReportValue's own docstring states once (it used to be
  // participationRate above, which is a calculated row now): no run can
  // overwrite a figure an operator typed, and the flat store they used to
  // read would hand it back under the next client's name. Beginning
  // Concentration keeps its own hook -- it is a definition rather than an
  // uncalculated figure.
  //
  // TWO rows here ARE calculated and take the localStorage hook every
  // calculated row uses, a run replacing whatever is under it: Overall
  // Diversified Performance, the buckets' aggregate weight-adjusted return
  // (task 3346), and Current Concentration (task 3348). Each also keeps its
  // own refusal in component state, the same split the Harvest card makes
  // for Unrealized Gains/Losses below -- and they refuse independently, so
  // one row going to N/A must not take the other with it.
  const [overallDiversifiedPerformance, setOverallDiversifiedPerformance] =
    useStrategyReportValue(
      "overall_diversified_performance_strategy",
      STRATEGY_REPORT_DEFAULTS.OVERALL_DIVERSIFIED_PERFORMANCE,
    );
  // Component state, not storage, for the reason unrealizedGainsUnavailable
  // below states: the reason belongs to the RUN, while the figure it
  // explains is persisted.
  const [
    overallDiversifiedPerformanceUnavailable,
    setOverallDiversifiedPerformanceUnavailable,
  ] = useState<string | undefined>(undefined);
  // Calculated as of task 2822 -- the sleeve IS Bucket 3, so the row is
  // that bucket's own performance figure -- so it moves off the
  // session-scoped holder onto the persisted hook every other calculated
  // row takes, and a run replaces whatever is under it. The holder exists
  // for rows nothing will ever overwrite (uncalculatedRowsNotStored says
  // so), and a run overwrites this one now.
  const [taxLossHarvestSleeve, setTaxLossHarvestSleeve] =
    useStrategyReportValue(
      "tax_loss_harvest_sleeve_strategy",
      STRATEGY_REPORT_DEFAULTS.TAX_LOSS_HARVEST_SLEEVE,
    );
  // Component state, not storage, on the same terms as the reason above:
  // the reason belongs to the RUN, while the figure it explains is
  // persisted. Held apart from the aggregate's reason even though the two
  // rows sit on one card, because the backend refuses each on its own
  // terms -- one row can show a number while the other shows a sentence.
  const [taxLossHarvestSleeveUnavailable, setTaxLossHarvestSleeveUnavailable] =
    useState<string | undefined>(undefined);
  // NOT useStrategyReportValue and NOT plain useState -- the hook carries the
  // "always 100%, but editable, and never the next client's baseline" rule
  // (operator, task 3141) and its own docstring says why each of those two
  // gets the lifetime wrong.
  const [beginningConcentration, setBeginningConcentration] =
    useBeginningConcentration(reportScopeKey);
  // The Diversify card's stock switch (operator, task 3508): the run's
  // per-stock concentrations, and which stock -- if any -- the card's two
  // concentration rows are currently measured for. Seeded at All Stocks,
  // which is the whole-of-Bucket-1 behaviour the card has always had.
  const [diversifyStocks, setDiversifyStocks] =
    useState<DiversifyStockConcentrations | null>(null);
  const [selectedDiversifyStock, setSelectedDiversifyStock] = useState(
    DIVERSIFY_ALL_STOCKS_OPTION,
  );
  // What the two rows show WHILE a stock is selected.
  //
  // Plain component state, never written through to either of the stores
  // the All Stocks figures live in, and that is the whole design of this
  // pair. The reader may type over a per-stock figure -- every row of
  // these cards takes an edit -- but the typed figure is DELIBERATELY NOT
  // REMEMBERED (operator, task 3508): switching stock, switching back, or
  // running the report again reseeds from the payload. Held in either
  // store it would be, and worse: `useStrategyReportValue` is one flat
  // object with no stock in its key, so a figure typed for AAPL would come
  // back under MSFT's name and reach a client's PDF as MSFT's opening
  // concentration -- the same failure as the Keep card's stale figures
  // (task 3155). Leaving it here also keeps the All Stocks figures
  // untouched underneath, so returning to All Stocks returns to the run's
  // own answer and the reader's own 100% baseline.
  const [stockBeginningConcentration, setStockBeginningConcentration] =
    useState<Pick<ReportValueCardProps, "value" | "unavailableReason">>({
      value: "",
    });
  const [stockCurrentConcentration, setStockCurrentConcentration] = useState<
    Pick<ReportValueCardProps, "value" | "unavailableReason">
  >({ value: "" });

  // Seeds both rows from the payload for one selection -- the ONE place a
  // per-stock figure is read, so the two rows can never be resolved on
  // different terms. All Stocks seeds nothing: those two rows are served
  // by the card's own stored concentration and the reader's baseline.
  const applyDiversifyStock = useCallback(
    (stocks: DiversifyStockConcentrations | null, symbol: string) => {
      if (symbol === DIVERSIFY_ALL_STOCKS_OPTION) return;
      setStockBeginningConcentration(
        diversifyStockFigure(
          stocks?.beginning_concentration_by_symbol ?? null,
          stocks?.beginning_concentration_by_symbol_unavailable ?? null,
          symbol,
        ),
      );
      setStockCurrentConcentration(
        diversifyStockFigure(
          stocks?.current_concentration_by_symbol ?? null,
          stocks?.current_concentration_by_symbol_unavailable ?? null,
          symbol,
        ),
      );
    },
    [],
  );

  const handleDiversifyStockChange = useCallback(
    (symbol: string) => {
      setSelectedDiversifyStock(symbol);
      applyDiversifyStock(diversifyStocks, symbol);
    },
    [diversifyStocks, applyDiversifyStock],
  );
  const [currentConcentration, setCurrentConcentration] =
    useStrategyReportValue(
      "current_concentration_strategy",
      STRATEGY_REPORT_DEFAULTS.CURRENT_CONCENTRATION,
    );
  // Component state, not storage, on the same terms as
  // unrealizedGainsUnavailable below: the reason belongs to the RUN, while
  // the figure it explains is persisted. A reload must not resurrect a
  // refusal from a run that is gone.
  const [currentConcentrationUnavailable, setCurrentConcentrationUnavailable] =
    useState<string | undefined>(undefined);
  // Calculated as of task 3349, so it takes the persisted hook every other
  // produced figure takes and a run replaces whatever is under it -- the
  // same split Current Concentration above and Unrealized Gains/Losses
  // below are on (uncalculatedRowsNotStored pins both sides).
  const [holdings, setHoldings] = useStrategyReportValue(
    "holdings_strategy",
    STRATEGY_REPORT_DEFAULTS.HOLDINGS,
  );
  // Component state, not storage, on the same terms as the reason above.
  const [holdingsUnavailable, setHoldingsUnavailable] = useState<
    string | undefined
  >(undefined);

  // MultiValuesCard hooks - Harvest Tax Benefits
  //
  // Split, and the split is the whole point: Unrealized Gains/Losses IS
  // calculated -- `_resolve_unrealized_gl` is the one figure of Harvest the
  // bucket report produces today -- so it keeps the localStorage hook every
  // other calculated row uses, and a run replaces whatever is under it. The
  // other five reach no calculation at all and are session-scoped for the
  // reason useUncalculatedReportValue states.
  const [realizedLosses, setRealizedLosses] = useUncalculatedReportValue(
    "realized_losses_strategy",
    STRATEGY_REPORT_DEFAULTS.REALIZED_LOSSES,
    reportScopeKey,
  );
  const [lossesUsed, setLossesUsed] = useUncalculatedReportValue(
    "losses_used_strategy",
    STRATEGY_REPORT_DEFAULTS.LOSSES_USED,
    reportScopeKey,
  );
  const [poolRemaining, setPoolRemaining] = useUncalculatedReportValue(
    "pool_remaining_strategy",
    STRATEGY_REPORT_DEFAULTS.POOL_REMAINING,
    reportScopeKey,
  );
  const [unrealizedGains, setUnrealizedGains] = useStrategyReportValue(
    "unrealized_gains_strategy",
    STRATEGY_REPORT_DEFAULTS.UNREALIZED_GAINS,
  );
  // Component state, not storage, and deliberately: the reason belongs to
  // the RUN, while the figure it explains is persisted. A reload or an
  // account switch must not resurrect a refusal from a run that is gone --
  // the same split the Keep card's rows make (keepRowFigure).
  const [unrealizedGainsUnavailable, setUnrealizedGainsUnavailable] = useState<
    string | undefined
  >(undefined);
  const [sharesSold, setSharesSold] = useUncalculatedReportValue(
    "shares_sold_strategy",
    STRATEGY_REPORT_DEFAULTS.SHARES_SOLD,
    reportScopeKey,
  );
  const [sellableToday, setSellableToday] = useUncalculatedReportValue(
    "sellable_today_strategy",
    STRATEGY_REPORT_DEFAULTS.SELLABLE_TODAY,
    reportScopeKey,
  );

  // The Harvest card's per-stock share counts, and which stock is showing.
  //
  // Component state, never written through the value hooks above, on the
  // Keep card's terms: the map belongs to the run that produced it, and a
  // map left standing would credit one period's counts to whatever period
  // is on screen. The PICK is component state for the same reason -- the
  // symbols come from the run, so a remembered symbol a new run does not
  // carry means nothing.
  //
  // The figures themselves DO go through the two hooks above rather than
  // into state, because those rows stay hand-editable: an operator's own
  // number waits underneath a refusal instead of being destroyed by it,
  // which is the rule every editable row on these cards follows.
  const [harvestStocks, setHarvestStocks] = useState<Record<
    string,
    HarvestStockCounts
  > | null>(null);
  const [selectedHarvestStock, setSelectedHarvestStock] = useState("");
  // Whether each row is waiting on a PICK rather than on a report. Held
  // apart from the figures, and never written through the value hooks,
  // exactly as the Keep card's row states are: writing the sentinel into
  // the holder would overwrite whatever an operator had typed into these
  // rows, and it would then read back as the sentinel.
  //
  // ONE FLAG PER ROW, not one for the pair: they are edited independently,
  // and a shared flag would mean typing into Shares Sold took Sellable
  // Today out of "Select stock" too -- revealing whatever figure had been
  // hand-typed into it, as if this run had produced it.
  const [sharesSoldPending, setSharesSoldPending] = useState(false);
  const [sellableTodayPending, setSellableTodayPending] = useState(false);
  // Whether the Sellable Today figure on screen is the BACKEND's, which is
  // what entitles it to the snapshot-date caption beside it. A hand-typed
  // figure is the reader's own, and a date asserting Orion measured it
  // would be false -- on the card and, through pdfLabel, on the client's
  // PDF. Tracked rather than inferred from the pending flag, because that
  // flag is cleared by a typed edit as well as by a pick.
  const [sellableTodayFromBackend, setSellableTodayFromBackend] =
    useState(false);
  const [sharesSoldReason, setSharesSoldReason] = useState<
    string | undefined
  >(undefined);
  const [sellableTodayReason, setSellableTodayReason] = useState<
    string | undefined
  >(undefined);
  // The day the sellable counts were read on -- a companion to them, and
  // outside the pick because every stock's count shares it.
  const [sellableAsOf, setSellableAsOf] = useState<string | null>(null);

  const applyHarvestStock = useCallback(
    (stocks: Record<string, HarvestStockCounts>, symbol: string) => {
      // Read as an OWN property: this map comes off the response, so a
      // symbol like "constructor" would otherwise resolve up the prototype
      // chain to a truthy function. The `typeof === "number"` guards below
      // already reject what that returns, so this makes the safety
      // independent of that reasoning rather than fixing a live defect.
      if (!Object.prototype.hasOwnProperty.call(stocks, symbol)) return;
      const counts = stocks[symbol];
      if (!counts) return;
      setSharesSoldPending(false);
      setSellableTodayPending(false);
      // ONLY a real figure is stored, and the reason is what empties the
      // row otherwise -- the rule the Unrealized G/L row follows, and for
      // the same reason: writing a null through these setters would
      // destroy a hand-entered number rather than merely hide it.
      if (typeof counts.shares_sold === "number") {
        setSharesSold(counts.shares_sold);
      }
      if (typeof counts.sellable_today === "number") {
        setSellableToday(counts.sellable_today);
      }
      // The caption follows the FIGURE: a refused count has no day to name
      // either, so this is false exactly when there is no backend number
      // on the row.
      setSellableTodayFromBackend(typeof counts.sellable_today === "number");
      // Re-read on every switch: each stock is refused on its own terms,
      // so a reason left standing from the previous ticker would explain
      // this one's blank with another one's cause.
      setSharesSoldReason(counts.unavailable?.shares_sold);
      setSellableTodayReason(counts.unavailable?.sellable_today);
    },
    [setSharesSold, setSellableToday],
  );

  const handleHarvestStockChange = useCallback(
    (symbol: string) => {
      setSelectedHarvestStock(symbol);
      if (harvestStocks) applyHarvestStock(harvestStocks, symbol);
    },
    [harvestStocks, applyHarvestStock],
  );

  // The operator typing a figure IS the figure (operator, task 2822), so
  // the backend's reason no longer describes what the row shows and must
  // go with it -- left standing it would hang a sentence about a refused
  // calculation off a number the reader just entered, and the row would
  // carry that contradiction into the client's PDF.
  const handleTaxLossHarvestSleeveChange = useCallback(
    (value: number | string) => {
      setTaxLossHarvestSleeveUnavailable(undefined);
      setTaxLossHarvestSleeve(value);
    },
    [setTaxLossHarvestSleeve],
  );

  // Typing over either row is the reader supplying the figure, so the
  // backend's reason no longer applies and the row stops waiting on a pick
  // -- the same rule as every other hand-editable row on these cards.
  const handleSharesSoldChange = useCallback(
    (value: number | string) => {
      setSharesSoldPending(false);
      setSharesSoldReason(undefined);
      setSharesSold(value);
    },
    [setSharesSold],
  );

  const handleSellableTodayChange = useCallback(
    (value: number | string) => {
      setSellableTodayPending(false);
      setSellableTodayReason(undefined);
      // From here the figure is the reader's own, so the snapshot date no
      // longer describes it.
      setSellableTodayFromBackend(false);
      setSellableToday(value);
    },
    [setSellableToday],
  );

  // The Keep card's values: the response carries one entry per Bucket 1
  // asset plus their total, and the option picked in the select next to
  // Shares End drives every field of the card.
  const [keepShares, setKeepShares] = useState<KeepThemedCardValues | null>(
    null,
  );
  const [selectedKeepAsset, setSelectedKeepAsset] = useState(KEEP_TOTAL_OPTION);
  // Which of the three states above the card's figures are in, seeded at
  // "unrun" because on a fresh mount that is exactly what is true: nothing
  // has run in this session, so the persisted figures on the rows belong to
  // whatever was last on screen and none of them is this period's result.
  //
  // Component state, never written through to storage, on the same terms as
  // the sentinel below: it is a fact about THIS session's run, and persisting
  // it would outlive the run it describes.
  const [keepFiguresState, setKeepFiguresState] =
    useState<KeepFiguresState>("unrun");
  // The rows the reader has typed into since the card's figures last changed.
  // keepRowFigure carries why this is per row rather than per card.
  const [keepRowsEdited, setKeepRowsEdited] =
    useState<ReadonlySet<KeepRowKey>>(NO_KEEP_ROWS_EDITED);
  const [taxSettingsAnchor, setTaxSettingsAnchor] =
    useState<HTMLElement | null>(null);

  const applyKeepShares = useCallback(
    (card: KeepThemedCardValues, option: string) => {
      const values =
        option === KEEP_TOTAL_OPTION ? card.total : card.assets[option];
      // Every row now carries THIS option's figure, so nobody's earlier edit
      // is on screen any more and none may keep claiming a row. It matters
      // most on an asset SWITCH: a row still marked edited would show the
      // figure set just below without the reason that came with it, so an
      // asset whose rate the backend refused would read as a silent blank
      // instead of an explained N/A.
      setKeepRowsEdited(NO_KEEP_ROWS_EDITED);
      setSharesStart(values.shares_start);
      setSharesEnd(values.shares_end);
      setValueStart(values.value_start);
      setValueEnd(values.value_end);
      setDividends(values.dividends);
      setBasis(values.basis);
      setTaxSavings(values.tax_savings);
      // The Participation Rate, the sentence saying why it has none and the
      // direction that labels it, applied TOGETHER and from this ONE option
      // -- which is what makes them a set the reader can trust, and why they
      // are read out of one call. keepParticipationFigure carries the rest.
      const participation = keepParticipationFigure(values);
      setParticipationRate(participation.rate);
      setParticipationUnavailable(participation.reason);
      setParticipationDirection(participation.direction);
    },
    [
      setSharesStart,
      setSharesEnd,
      setValueStart,
      setValueEnd,
      setDividends,
      setBasis,
      setTaxSavings,
      setParticipationRate,
    ],
  );

  // Typing into a row is the reader supplying the figure this run did not, so
  // THAT ROW ALONE stops reading its seed or its N/A and shows what they
  // typed. Only the mark is held here -- the VALUE persists through the row's
  // own storage hook, exactly as it always did.
  const markKeepRowEdited = useCallback((key: KeepRowKey) => {
    setKeepRowsEdited((edited) =>
      edited.has(key) ? edited : new Set(edited).add(key),
    );
  }, []);

  // A Keep row's setter with the mark above in front of it, so every row of
  // the card takes an edit the same way: the binding is what stops one row's
  // handler being written differently from the next.
  const keepRowChange = useCallback(
    (key: KeepRowKey, setValue: (value: number | string) => void) =>
      (value: number | string) => {
        markKeepRowEdited(key);
        setValue(value);
      },
    [markKeepRowEdited],
  );

  const handleKeepAssetChange = useCallback(
    (option: string) => {
      setSelectedKeepAsset(option);
      if (keepShares) applyKeepShares(keepShares, option);
    },
    [keepShares, applyKeepShares],
  );

  // The Protect card's per-asset max stock drop: the response returns a
  // dictionary keyed by symbol, and the asset picked in the select next
  // to Max Stock Drop drives which asset's drop the field shows.
  const [protectDrops, setProtectDrops] = useState<ProtectDrops | null>(null);
  const [selectedProtectAsset, setSelectedProtectAsset] = useState("");

  const applyProtectDrop = useCallback(
    (drops: ProtectDrops, symbol: string) => {
      setMaxStockDrop(drops[symbol] ?? 0);
    },
    [setMaxStockDrop],
  );

  const handleProtectAssetChange = useCallback(
    (symbol: string) => {
      setSelectedProtectAsset(symbol);
      if (protectDrops) applyProtectDrop(protectDrops, symbol);
    },
    [protectDrops, applyProtectDrop],
  );

  // The calculated themed-card values → the Keep/Protect cards, plus the
  // Harvest card's one calculated row (Unrealized Gains/Losses).
  // available_margin_balance is the one reserved-for-now zero left and is
  // deliberately NOT applied, so that field keeps its stored value until a
  // real calculation lands; participation_rate stopped being one in task
  // 3345 and is applied with the rest of the Keep card now.
  // The per-asset dictionaries (keep values, max_stock_drop) are applied
  // by their own dropdown flows; oea_drop is null when no bucket has
  // daily values, the Harvest unrealized gain/loss is null when the
  // Bucket 3 account has no position snapshot, and the Diversify TWRs are
  // null when their scope has no data — the stored value stays in those
  // cases too.
  const applyThemedCards = useCallback(
    (themedCards: BucketThemedCards) => {
      const { keep, protect, harvest, diversify } = themedCards;
      const card = keep?.assets ? keep : null;
      setKeepShares(card);
      // WHICH of the two answers this run gave about Bucket 1, and the reason
      // it is recorded rather than inferred from `keepShares` at the row.
      // This is the ONE transition that can take the card out of "unrun", so
      // it is also the only place that can tell "a run carried no Bucket 1
      // data" apart from "no run has happened yet" — and those two get
      // different words on the rows (KeepFiguresState says which and why).
      setKeepFiguresState(card ? "figures" : "absent");
      if (card) {
        // Keep the previously picked option when the new results still
        // carry it; otherwise fall back to the total.
        const option = card.assets[selectedKeepAsset]
          ? selectedKeepAsset
          : KEEP_TOTAL_OPTION;
        setSelectedKeepAsset(option);
        applyKeepShares(card, option);
      } else {
        // A run that carried no Bucket 1 data leaves every Keep row reading
        // N/A behind KEEP_NO_BUCKET_1, which the card state set just above is
        // what does. It USED to leave the previous run's stored figures
        // standing here instead — the whole of task 3155, and the reason the
        // operator saw a 472.39% rate under a card header with no stock
        // picker in it.
        //
        // What has to happen here is the reader's own edits being dropped:
        // they belong to the run this one has just replaced.
        setKeepRowsEdited(NO_KEEP_ROWS_EDITED);
      }
      if (protect) {
        if (protect.oea_drop != null) {
          setOeaDrop(protect.oea_drop);
        }
        setCollarPL(protect.collar_pl);
      }
      // Current Concentration, on exactly the terms unrealized_gain_loss below
      // is applied on -- the two are the only calculated rows on these two
      // cards, and they share the figure/reason pair the backend sends.
      //
      // A null is NOT written over the stored figure: a refusal must not
      // destroy a hand-entered number. It is not left standing in silence
      // either -- the reason goes into component state and the row renders
      // N/A behind it, so a stored figure cannot pass for this run's result.
      // `!= null` and not `!`, so a genuine 0% concentration (an account
      // that has fully diversified out of the stock) is applied rather than
      // swallowed as falsy.
      if (diversify?.current_concentration != null) {
        setCurrentConcentration(diversify.current_concentration);
      }
      // The card's per-stock concentrations, and the selection they are
      // read under (task 3508). The previously picked stock is KEPT when
      // this run still carries it -- the reader is usually running the
      // same report over a new period and is asking about the same
      // stock -- and otherwise the card falls back to All Stocks rather
      // than to a stock nobody chose. The same rule, for the same reason,
      // as the Keep card's option above.
      //
      // Reseeding through applyDiversifyStock is also what DISCARDS a
      // per-stock figure the reader had typed: it belongs to the run this
      // one has just replaced.
      const diversifyStockPayload: DiversifyStockConcentrations | null = diversify
        ? {
            current_concentration_by_symbol:
              diversify.current_concentration_by_symbol ?? null,
            current_concentration_by_symbol_unavailable:
              diversify.current_concentration_by_symbol_unavailable ?? null,
            beginning_concentration_by_symbol:
              diversify.beginning_concentration_by_symbol ?? null,
            beginning_concentration_by_symbol_unavailable:
              diversify.beginning_concentration_by_symbol_unavailable ?? null,
          }
        : null;
      setDiversifyStocks(diversifyStockPayload);
      const stockOption = card?.assets[selectedDiversifyStock]
        ? selectedDiversifyStock
        : DIVERSIFY_ALL_STOCKS_OPTION;
      setSelectedDiversifyStock(stockOption);
      applyDiversifyStock(diversifyStockPayload, stockOption);
      // Outside the guard above, and it has to be: a run that PRODUCED the
      // figure must clear the previous run's refusal, or the row keeps
      // reading N/A over a figure this run measured.
      //
      // `current_concentration_unavailable`, NOT the plain `unavailable`
      // beside it -- that one is the aggregate return's (types.ts says
      // which is whose). The two rows refuse independently, and reading one
      // key for both put this row into N/A every time the OTHER figure was
      // the one the run could not produce.
      setCurrentConcentrationUnavailable(
        diversify?.current_concentration_unavailable ?? undefined,
      );
      // Holdings, on exactly the terms above. `!= null` and not `!` carries
      // more weight on this row than on the percentages beside it: a count
      // of 0 is this client holding nothing in either bucket, which is an
      // ANSWER, and it is also what every absent value the numeric pipeline
      // coerces comes out as -- so a falsy test would drop the real 0 and
      // leave the row on the seed, telling an operator to run the report
      // they had just run.
      if (diversify?.holdings != null) {
        setHoldings(diversify.holdings);
      }
      // `holdings_unavailable`, NOT the plain `unavailable` -- that one is
      // the aggregate return's. Outside the guard for the same reason as
      // above: a run that produced the count must clear the last refusal.
      setHoldingsUnavailable(diversify?.holdings_unavailable ?? undefined);
      // Account-wide, so it survives a run that carried no Bucket 1 data.
      // A null is NOT applied over the stored value -- a refusal must not
      // destroy a hand-entered figure -- but it is not left standing in
      // silence either: the reason goes into component state and the row
      // renders N/A behind it, so a stored number cannot pass for this
      // run's result. `!= null` and not `!`, so a genuine net-zero total
      // is applied rather than swallowed as falsy.
      if (harvest?.unrealized_gain_loss != null) {
        setUnrealizedGains(harvest.unrealized_gain_loss);
      }
      setUnrealizedGainsUnavailable(harvest?.unavailable ?? undefined);
      // Bucket 2 and Bucket 3's aggregate return (task 3573 excluded
      // Bucket 1 from its terms), on exactly the terms above: a refusal
      // does not destroy the stored figure, but it does not leave it
      // standing in silence either -- the reason goes into component
      // state and the row renders N/A behind it. `!= null` and not `!`,
      // so a pair that genuinely broke even is applied as the 0% it is
      // rather than swallowed as falsy.
      if (diversify?.overall_diversified_performance != null) {
        setOverallDiversifiedPerformance(
          diversify.overall_diversified_performance,
        );
      }
      setOverallDiversifiedPerformanceUnavailable(
        diversify?.unavailable ?? undefined,
      );
      // The sleeve, on exactly those terms -- and its OWN reason key, not
      // the plain `unavailable`, which is the aggregate's. `!= null` and
      // not `!`, so a Bucket 3 that genuinely went nowhere is applied as
      // the 0% it is rather than swallowed as falsy. The reason is set
      // outside the guard so a run that produced the figure clears the
      // last refusal.
      if (diversify?.tax_loss_harvest_sleeve != null) {
        setTaxLossHarvestSleeve(diversify.tax_loss_harvest_sleeve);
      }
      setTaxLossHarvestSleeveUnavailable(
        diversify?.tax_loss_harvest_sleeve_unavailable ?? undefined,
      );
      // The per-stock counts, and the pick they are read with.
      //
      // `stocks` is `{}` when the run named no Bucket 3 or the bucket has
      // no members, which is an ANSWER and not an absence -- so the empty
      // map is stored, the select has nothing to offer and is not
      // rendered, and the two rows stay on their seed. A result cached
      // before these rows shipped carries no `stocks` key at all, and THAT
      // is the absence: it leaves the rows exactly as they were.
      const stocks = harvest?.stocks ?? null;
      setHarvestStocks(stocks);
      setSellableAsOf(harvest?.sellable_as_of ?? null);
      const symbols = stocks ? Object.keys(stocks) : [];
      // Keep the previously picked stock when this run still carries it --
      // the Keep card's rule, and what makes re-running a report for a new
      // period not throw away where the reader was looking.
      const pick =
        selectedHarvestStock &&
        stocks &&
        Object.prototype.hasOwnProperty.call(stocks, selectedHarvestStock)
          ? selectedHarvestStock
          : "";
      setSelectedHarvestStock(pick);
      if (pick && stocks) {
        applyHarvestStock(stocks, pick);
      } else {
        // Waiting on a pick, and ONLY when there is something to pick: a
        // run that carried no stocks at all has nothing for the reader to
        // do, so telling them to select a stock would be a dead end.
        setSharesSoldPending(symbols.length > 0);
        setSellableTodayPending(symbols.length > 0);
        setSellableTodayFromBackend(false);
        setSharesSoldReason(undefined);
        setSellableTodayReason(undefined);
      }
    },
    [
      selectedKeepAsset,
      applyKeepShares,
      setOeaDrop,
      setCollarPL,
      setCurrentConcentration,
      setUnrealizedGains,
      setOverallDiversifiedPerformance,
      setTaxLossHarvestSleeve,
      setHoldings,
      selectedHarvestStock,
      applyHarvestStock,
      selectedDiversifyStock,
      applyDiversifyStock,
    ],
  );

  // Update keepValues with hooks
  //
  // EVERY row reads its figure through keepRowFigure rather than off its
  // storage hook directly, and that is the whole of task 3155's fix: a stored
  // figure is only this run's result while the card is in the "figures"
  // state, and a row that renders one at any other time is asserting that
  // this run produced it. The stored value is never overwritten to achieve
  // that — it waits underneath, so an operator's hand-entered number survives
  // a run that could not replace it.
  const keepValuesWithHooks: ReportValueCardProps[] = [
    {
      label: StrategyReportThemedCardValuesLabels.SHARES_START,
      ...keepRowFigure(
        keepFiguresState,
        keepRowsEdited.has("shares_start"),
        sharesStart,
      ),
      type: SingleValueCardType.NUMBER,
      onValueChange: keepRowChange("shares_start", setSharesStart),
    },
    {
      label: StrategyReportThemedCardValuesLabels.SHARES_END,
      ...keepRowFigure(
        keepFiguresState,
        keepRowsEdited.has("shares_end"),
        sharesEnd,
      ),
      type: SingleValueCardType.NUMBER,
      onValueChange: keepRowChange("shares_end", setSharesEnd),
    },
    {
      label: StrategyReportThemedCardValuesLabels.VALUE_START,
      ...keepRowFigure(
        keepFiguresState,
        keepRowsEdited.has("value_start"),
        valueStart,
      ),
      type: SingleValueCardType.CURRENCY,
      onValueChange: keepRowChange("value_start", setValueStart),
    },
    {
      label: StrategyReportThemedCardValuesLabels.VALUE_END,
      ...keepRowFigure(
        keepFiguresState,
        keepRowsEdited.has("value_end"),
        valueEnd,
      ),
      type: SingleValueCardType.CURRENCY,
      onValueChange: keepRowChange("value_end", setValueEnd),
    },
    {
      label: StrategyReportThemedCardValuesLabels.DIVIDENDS,
      ...keepRowFigure(
        keepFiguresState,
        keepRowsEdited.has("dividends"),
        dividends,
      ),
      type: SingleValueCardType.CURRENCY,
      onValueChange: keepRowChange("dividends", setDividends),
    },
    {
      // Labelled by the direction only while the row shows THIS run's own
      // figure: "unrun" and "absent" hide the figure the direction belongs
      // to, and a reader's edit replaces it, so none of them keeps its label
      // -- the same precedence keepRowFigure gives the reason.
      label: keepParticipationLabel(
        keepFiguresState === "figures" &&
          !keepRowsEdited.has("participation_rate")
          ? participationDirection
          : undefined,
      ),
      // The one row of this card that carries a reason of its own, so it is
      // the one that passes keepRowFigure's fourth argument. Everything else
      // about it is the card's ordinary machinery: "figures" pairs the stored
      // rate with the sentence, "absent"/"unrun" outrank both, and an edit
      // outranks all of them (keepRowFigure says why for each).
      ...keepRowFigure(
        keepFiguresState,
        keepRowsEdited.has("participation_rate"),
        participationRate,
        participationUnavailable,
      ),
      type: SingleValueCardType.PERCENTAGE,
      onValueChange: keepRowChange("participation_rate", setParticipationRate),
    },
    {
      label: StrategyReportThemedCardValuesLabels.BASIS,
      ...keepRowFigure(keepFiguresState, keepRowsEdited.has("basis"), basis),
      type: SingleValueCardType.CURRENCY,
      onValueChange: keepRowChange("basis", setBasis),
    },
    {
      label: StrategyReportThemedCardValuesLabels.TAX_SAVINGS,
      ...keepRowFigure(
        keepFiguresState,
        keepRowsEdited.has("tax_savings"),
        taxSavings,
      ),
      type: SingleValueCardType.CURRENCY,
      onValueChange: keepRowChange("tax_savings", setTaxSavings),
      labelAction: (
        <>
          <IconButton
            size="small"
            aria-label="Tax savings settings"
            sx={{ p: 0.25, fontSize: 16 }}
            onClick={(event) => setTaxSettingsAnchor(event.currentTarget)}
          >
            <SettingsIcon fontSize="inherit" />
          </IconButton>
          <Popover
            open={Boolean(taxSettingsAnchor)}
            anchorEl={taxSettingsAnchor}
            onClose={() => setTaxSettingsAnchor(null)}
            anchorOrigin={{ vertical: "bottom", horizontal: "left" }}
          >
            <Box sx={{ p: 2, width: 320 }}>
              <StrategyTaxStateSetting />
              <StrategyFederalTaxSetting />
            </Box>
          </Popover>
        </>
      ),
    },
  ];

  // Update protectValues with hooks
  const protectValuesWithHooks: ReportValueCardProps[] = [
    {
      label: StrategyReportThemedCardValuesLabels.MAX_STOCK_DROP,
      value: maxStockDrop,
      type: SingleValueCardType.PERCENTAGE,
      onValueChange: setMaxStockDrop,
    },
    {
      label: StrategyReportThemedCardValuesLabels.OEA_DROP,
      value: oeaDrop,
      type: SingleValueCardType.PERCENTAGE,
      onValueChange: setOeaDrop,
    },
    {
      label: StrategyReportThemedCardValuesLabels.COLLAR_PL,
      value: collarPL,
      type: SingleValueCardType.CURRENCY,
      onValueChange: setCollarPL,
    },
    {
      label: StrategyReportThemedCardValuesLabels.AVAILABLE_MARGIN_BALANCE,
      // No figure until the operator types one -- null, which the card renders
      // as the muted N/A and the export drops -- and then their figure, in the
      // figure treatment and on the client's copy. The value says which of the
      // two it is on its own, so nothing rides beside it. It comes from
      // useAvailableMarginBalance.
      ...availableMarginBalanceRow,
      type: SingleValueCardType.CURRENCY,
      onValueChange: handleAvailableMarginBalanceChange,
      // Emptying the box is the way back to N/A, and this row is the only one
      // on the report that has a way back to want: every other field here is
      // persisted, where clearing is a slip rather than an instruction. Same
      // call as the per-run reset below, because it is the same thing --
      // nobody has entered a balance.
      onClear: resetAvailableMarginBalance,
    },
  ];

  // The row's two props and the donuts' figure, resolved ONCE -- see
  // currentConcentrationFigure for why the row and the chart may not decide
  // this separately. Resolved here rather than at the call sites below so
  // the screen donut and the PDF's read the same value too.
  //
  // The donut plots a share of 100 and cannot draw a seed, which is why the
  // figure is null rather than 0 whenever there is none: a 0 fallback would
  // turn a "No Report" row into a complete ring asserting a fully
  // diversified account -- a made-up figure in a chart instead of in a
  // field.
  const currentConcentrationRow = currentConcentrationFigure(
    currentConcentration,
    currentConcentrationUnavailable,
  );
  const currentConcentrationPct = currentConcentrationRow.pct;

  // Whether the card's two concentration rows are measured for one stock
  // rather than for the whole of Bucket 1. Read once here so the rows, the
  // labels and the export cannot each decide it differently.
  const diversifyStockSelected =
    selectedDiversifyStock !== DIVERSIFY_ALL_STOCKS_OPTION;

  // The stocks the card's switch offers: the Bucket 1 assets THIS run
  // produced, which is the same list the Keep card's own switch is built
  // from and so the one place "the stocks in Bucket 1" is decided. Read
  // from the Keep card rather than from the concentration maps beside it
  // so that a run whose concentration was REFUSED still offers its stocks
  // -- the reader then gets an explained N/A for the stock they asked
  // about, instead of a dropdown that silently lost its options.
  const diversifyStockOptions = useMemo(
    () => Object.keys(keepShares?.assets ?? {}),
    [keepShares],
  );

  // Update diversifyValues with hooks
  //
  // Parameterised by the stock SELECTION, because this card is rendered
  // twice from two different ones: the screen shows whatever the switch
  // says, and the client's PDF always shows the whole of Bucket 1 (task
  // 3508). One builder rather than two lists, so the three rows the
  // switch does not touch cannot come to differ between paper and screen.
  const diversifyRows = (stock: string): ReportValueCardProps[] => {
  const stockSelected = stock !== DIVERSIFY_ALL_STOCKS_OPTION;
  return [
    {
      label: StrategyReportThemedCardValuesLabels.OVERALL_DIVERSIFIED_PERFORMANCE,
      // The empty sentinel WITH the reason is what renders a muted "N/A"
      // behind an info icon, exactly as Unrealized Gains/Losses does. The
      // stored figure waits underneath, so a refusal costs the reader
      // nothing but the false claim that it is this run's answer -- which
      // on a PERCENTAGE row would have read as "the portfolio went
      // precisely nowhere".
      value: overallDiversifiedPerformanceUnavailable
        ? ""
        : overallDiversifiedPerformance,
      unavailableReason: overallDiversifiedPerformanceUnavailable,
      type: SingleValueCardType.PERCENTAGE,
      onValueChange: setOverallDiversifiedPerformance,
    },
    {
      label: StrategyReportThemedCardValuesLabels.TAX_LOSS_HARVEST_SLEEVE,
      // The empty sentinel WITH the reason, the pair every refused figure
      // on these cards renders: a muted "N/A" behind an info icon, the row
      // dropped from the client-facing PDF, and the stored figure waiting
      // underneath rather than passing for this run's answer -- which on a
      // PERCENTAGE row would have read as "the sleeve went precisely
      // nowhere".
      value: taxLossHarvestSleeveUnavailable ? "" : taxLossHarvestSleeve,
      unavailableReason: taxLossHarvestSleeveUnavailable,
      type: SingleValueCardType.PERCENTAGE,
      onValueChange: handleTaxLossHarvestSleeveChange,
    },
    {
      // Both concentration rows name the stock they were measured for
      // when one is selected (task 3508), and keep the plain label on All
      // Stocks -- which is the selection, the figure and the meaning the
      // card has always had.
      label: concentrationRowLabel(
        StrategyReportThemedCardValuesLabels.BEGINNING_CONCENTRATION,
        stock,
      ),
      // All Stocks is the reader's own 100% baseline and stays on its own
      // hook; a selected stock reads the run's figure out of component
      // state, where a typed override deliberately does not outlive the
      // selection (the state's own comment says why).
      ...(stockSelected
        ? stockBeginningConcentration
        : { value: beginningConcentration }),
      type: SingleValueCardType.PERCENTAGE,
      onValueChange: stockSelected
        ? (value: number | string) =>
            setStockBeginningConcentration({ value })
        : setBeginningConcentration,
    },
    {
      label: concentrationRowLabel(
        StrategyReportThemedCardValuesLabels.CURRENT_CONCENTRATION,
        stock,
      ),
      // All Stocks: the figure and any refusal together, from the one
      // resolution the donut beside this row reads as well. `pct` is the
      // donut's half of that answer and is not a card prop, so it is
      // dropped here.
      //
      // A selected stock does NOT feed that donut and is not read through
      // the same resolution: the chart stays the whole-of-Bucket-1
      // allocation whatever the dropdown says (operator, task 3508), so
      // the two answers it has to agree with are the stored figure and
      // the stored reason -- exactly the ones it already reads.
      ...(stockSelected
        ? stockCurrentConcentration
        : {
            value: currentConcentrationRow.value,
            unavailableReason: currentConcentrationRow.unavailableReason,
          }),
      type: SingleValueCardType.PERCENTAGE,
      onValueChange: stockSelected
        ? (value: number | string) => setStockCurrentConcentration({ value })
        : setCurrentConcentration,
    },
    {
      label: StrategyReportThemedCardValuesLabels.HOLDINGS,
      // The empty sentinel WITH the reason, the pair every refused figure on
      // these cards renders: a muted "N/A" behind an info icon, the row
      // dropped from the client-facing PDF, and the stored count waiting
      // underneath rather than passing for this run's answer.
      value: holdingsUnavailable ? "" : holdings,
      unavailableReason: holdingsUnavailable,
      type: SingleValueCardType.NUMBER,
      onValueChange: setHoldings,
    },
  ];
  };

  const diversifyValuesWithHooks = diversifyRows(selectedDiversifyStock);

  // What the client's PDF prints for the Diversify card: THE SAME ROWS
  // BUILT FOR ALL STOCKS, whatever the switch says on screen (operator,
  // task 3508).
  //
  // The stock switch is a screen-side lens and the export is the one
  // document that leaves the building, so a figure measured for one stock
  // must never print there labelled as the whole bucket -- beside a donut
  // still drawn from the all-stocks figure, which would leave the paper
  // disagreeing with itself.
  //
  // The BUILDER again rather than a second list: the three rows the switch
  // does not touch are then the same rows by construction, and a row added
  // to this card reaches paper and screen together or not at all.
  const diversifyPdfValues = diversifyStockSelected
    ? diversifyRows(DIVERSIFY_ALL_STOCKS_OPTION)
    : diversifyValuesWithHooks;

  const sellableAsOfText = useMemo(
    () => sellableAsOfCaption(sellableAsOf),
    [sellableAsOf],
  );

  // The stocks this run offers. Already alphabetical -- the bucket's
  // selection is ordered by symbol and that order survives to the
  // response's object keys.
  const harvestStockOptions = useMemo(
    () => Object.keys(harvestStocks ?? {}),
    [harvestStocks],
  );

  // Update harvestTaxBenefitsValues with hooks
  const harvestTaxBenefitsValuesWithHooks: ReportValueCardProps[] = [
    {
      label: StrategyReportThemedCardValuesLabels.REALIZED_LOSSES,
      value: realizedLosses,
      type: SingleValueCardType.CURRENCY,
      onValueChange: setRealizedLosses,
    },
    {
      label: StrategyReportThemedCardValuesLabels.LOSSES_USED,
      value: lossesUsed,
      type: SingleValueCardType.CURRENCY,
      onValueChange: setLossesUsed,
    },
    {
      label: StrategyReportThemedCardValuesLabels.POOL_REMAINING,
      value: poolRemaining,
      type: SingleValueCardType.CURRENCY,
      onValueChange: setPoolRemaining,
    },
    {
      label: StrategyReportThemedCardValuesLabels.UNREALIZED_GAINS,
      // The empty sentinel WITH the reason is what renders a muted "N/A"
      // behind an info icon and what drops the row from the client-facing
      // PDF. The stored figure waits underneath it, so a refusal costs the
      // reader nothing but the false claim that it is this run's answer.
      value: unrealizedGainsUnavailable ? "" : unrealizedGains,
      unavailableReason: unrealizedGainsUnavailable,
      type: SingleValueCardType.CURRENCY,
      onValueChange: setUnrealizedGains,
    },
    {
      label: StrategyReportThemedCardValuesLabels.SHARES_SOLD,
      // Three states, read in the order the card resolves them. A REFUSAL
      // outranks the rest and empties the row -- the Unrealized G/L row's
      // rule, and MultiValuesCard's: the reason renders N/A behind an info
      // icon, and the stored figure waits underneath rather than passing for
      // this stock's count. Then "waiting on a pick", which only a run
      // carrying stocks can be in. The seed underneath is reached only when
      // this run carried no stocks to pick from at all.
      value: sharesSoldReason
        ? ""
        : sharesSoldPending
          ? SELECT_STOCK_DISPLAY
          : sharesSold,
      type: SingleValueCardType.NUMBER,
      onValueChange: handleSharesSoldChange,
      unavailableReason: sharesSoldReason,
    },
    // The select the two rows around it are read with, on its own row
    // BETWEEN them because that is where the operator asked for it (task
    // 3145) -- and it reads as the thing driving both, which it would not
    // from either row's own value slot.
    //
    // PRESENT ONLY WHEN THERE IS SOMETHING TO PICK. Before any run, and for
    // a bucket with no members, an empty select is a control offering an
    // action that cannot be taken -- beside two rows already saying "No
    // Report", which is the honest account of that state on its own. The
    // Keep card's header picker appears on the same condition.
    ...(harvestStockOptions.length === 0
      ? []
      : [
          {
            label: StrategyReportThemedCardValuesLabels.BUCKET_3_STOCK,
            // No figure here at all; `control` is what renders, and the row
            // is absent from the PDF export. The value is required by the
            // row type and never read.
            value: "",
            type: SingleValueCardType.NUMBER,
            onValueChange: () => {},
            // FULL WIDTH, and that is what puts it BETWEEN the two rows
            // rather than beside one of them. The card is a two-column
            // grid, so a half-width row here would land in the right-hand
            // cell of the Shares Sold row and push Sellable Today onto the
            // next line -- which reads as a select belonging to Shares Sold
            // alone, the opposite of what it does. Taking the whole width
            // forces its own line and leaves the two figures stacked above
            // and below it, which is the arrangement the operator asked for
            // and the only one in which the control visibly governs both.
            fullWidthRow: true,
            control: (
              <Select
                size="small"
                displayEmpty
                value={selectedHarvestStock}
                onChange={(event) =>
                  handleHarvestStockChange(event.target.value)
                }
                inputProps={{
                  "aria-label":
                    StrategyReportThemedCardValuesLabels.BUCKET_3_STOCK,
                }}
                // Wide enough for a five-character ticker and for the
                // placeholder that stands in for one, so the control does
                // not resize as the reader moves between stocks. Small
                // enough to leave the row it occupies uncrowded.
                sx={{ minWidth: 140 }}
              >
                <MenuItem value="" disabled>
                  {SELECT_STOCK_DISPLAY}
                </MenuItem>
                {harvestStockOptions.map((symbol) => (
                  <MenuItem key={symbol} value={symbol}>
                    {symbol}
                  </MenuItem>
                ))}
              </Select>
            ),
          },
        ]),
    {
      label: StrategyReportThemedCardValuesLabels.SELLABLE_TODAY,
      // Same three states, same order, same reason -- see Shares Sold above.
      value: sellableTodayReason
        ? ""
        : sellableTodayPending
          ? SELECT_STOCK_DISPLAY
          : sellableToday,
      type: SingleValueCardType.NUMBER,
      onValueChange: handleSellableTodayChange,
      unavailableReason: sellableTodayReason,
      // The day the count was taken, beside the figure. It appears only
      // with a BACKEND count: a date beside the "Select stock" placeholder
      // would describe a figure nobody has asked for yet, and beside a
      // hand-typed one it would credit the reader's own number to an Orion
      // snapshot.
      action:
        sellableAsOfText && sellableTodayFromBackend ? (
          <Typography
            variant="caption"
            sx={{ color: "text.secondary", whiteSpace: "nowrap" }}
          >
            {sellableAsOfText}
          </Typography>
        ) : undefined,
      // The PDF has no place for the caption, and "Sellable Today" on paper
      // with no date is the one claim on this card a reader cannot check.
      pdfLabel:
        sellableAsOfText && sellableTodayFromBackend
          ? `${SELLABLE_TODAY_PDF_LABEL} (${sellableAsOfText})`
          : undefined,
    },
  ];


  // The PDF donut's two slices, clamped once here rather than at each of the
  // two entries below -- one call, so the pair cannot be built from two
  // different readings of the same figure.
  const allocationPdfSlices =
    currentConcentrationPct === null
      ? null
      : allocationSlices(currentConcentrationPct);

  // The drawdown chart's two bars, read once for the PDF on the same terms as
  // the donut's slices above: null when the row holds no figure, so the
  // entries below can be DROPPED rather than zeroed. `parseFloat(...) || 0`
  // stood at each of those two entries and put a 0.0% bar on a client's paper
  // for every "No Report" row -- a portfolio that did not fall at all,
  // printed as a result. The screen chart refuses the same claim its own way
  // (drawdownRowFigure): it cannot drop a row, so it says the state instead.
  const maxStockDropPct = toFiniteNumber(maxStockDrop);
  const oeaDropPct = toFiniteNumber(oeaDrop);

  // The chart's y-axis bounds, derived from THIS protect asset's own two
  // drops and stored per asset (OAM-648) -- a stock that fell 60% and one
  // that fell 3% no longer share one 20% axis. The readings above are what
  // the derivation sees, so a row with no figure takes no part in it rather
  // than standing in it as a 0.
  const [maxDropLimits, setMaxDropLimits] = useMaxDropLimits(
    selectedProtectAsset,
    maxStockDropPct,
    oeaDropPct,
  );
  const [isTransactionModalOpen, setIsTransactionModalOpen] = useState(false);
  // The bucket report intentionally has no exclude_transactions yet (batch
  // contract kept minimal until the aggregation phase) — deselection is
  // collected here so the modal keeps working, but it doesn't affect the
  // calculation for now.
  const [deselectedTransactions, setDeselectedTransactions] = useState<
    OrionTransaction[]
  >([]);
  const [getReportPdf, { isLoading: isPdfLoading }] = useGetReportPdfMutation();
  // The series the performance chart is drawing, reported up by ChartsReport
  // so the PDF prints the chart the reader is looking at.
  const [chartSpecs, setChartSpecs] = useState<ChartSpec[]>([]);
  const { settingsInfo } = useReportSettings();
  // Both rates are edited in the Keep card's tax settings and sent with
  // Calculate, where they drive that card's tax savings.
  const [federalTaxRate] = useFederalTaxRate();
  const { stateTaxRate } = useStateTaxRate(settingsInfo.client_id ?? null);
  // Strategy reports run over the account's persisted Bucket 1/2 — no
  // Strategy selection involved. This phase produces the combined excel
  // (one sheet per pair) + per-pair statuses; the cards/charts keep their
  // stored values until bucket aggregation lands.
  const [startBucketReportJob] = useStartBucketReportJobMutation();
  const [fetchBucketJobStatus] = useLazyGetBucketReportJobStatusQuery();
  const [isCalculating, setIsCalculating] = useState(false);
  const [calculationError, setCalculationError] = useState("");
  const [pairFailures, setPairFailures] = useState<BucketPairReportStatus[]>(
    [],
  );
  const [excelUrl, setExcelUrl] = useState<string | null>(null);
  const [bucketTotals, setBucketTotals] = useState<BucketTotals | null>(null);
  // Why the selected scope has no time-weighted return, when it has none.
  //
  // Plain state rather than `useStrategyReportValue`, and that is the same
  // lifetime every other backend-sent sentence on this page has: it belongs
  // to ONE run's payload, which `bucketTotals` above also holds in state, so
  // persisting the reason past the run it describes would explain a figure
  // this session no longer has. The card degrades honestly when the report
  // unmounts and takes both with it -- the stored value is cleared to "" in
  // applyBucketCards below, so what comes back is a bare N/A rather than the
  // 0.00% this task exists to stop showing.
  const [timeWeightedReturnReason, setTimeWeightedReturnReason] = useState<
    string | undefined
  >(undefined);
  // The sentence the three value cards carry when the Long Value scope could
  // not be given them (operator, task 3636), held on exactly the terms the
  // return's reason above is: it belongs to ONE run's payload, so persisting
  // it past the run it describes would explain figures this session no
  // longer has. The three stored values are cleared to "" beside it in
  // applyBucketCards, so what a remount brings back is a bare N/A rather
  // than the previous run's opening balance.
  const [valueCardsReason, setValueCardsReason] = useState<string | undefined>(
    undefined,
  );
  // Where the two value figures were measured, when the scope's bucket did
  // not cover the whole period (operator, task 3730) -- summaryValueDateNotes
  // above builds the sentences and says when there are any.
  //
  // Session-only on the same terms as the reason above and the return's
  // reason before it: the dates belong to ONE run's payload, so a note that
  // survived a remount would explain a figure this session no longer has --
  // and unlike those two it sits beside a figure that IS there, so a stale
  // one would misdate a number the reader can read.
  const [startingValueDateNote, setStartingValueDateNote] = useState<
    string | undefined
  >(undefined);
  const [endingValueDateNote, setEndingValueDateNote] = useState<
    string | undefined
  >(undefined);
  const [selectedSummary, setSelectedSummary] = useState<BucketSummaryEnum>(
    BucketSummaryEnum.BUCKET_1,
  );
  // The portfolio's aggregate weight-adjusted return AS THIS RUN REPORTED IT,
  // held for the Long Value scope's card_4 (task 3542).
  //
  // A second holder of a figure the Diversify card already stores, and it has
  // to be, for two reasons the stored one cannot serve. Its LIFETIME is this
  // payload's: the stored figure is persisted and survives a remount, so
  // reading it here would credit Long Value with the return of a run whose
  // buckets are no longer on screen -- the same rule the refusal beside it
  // already follows, and the same one `timeWeightedReturnReason` above keeps.
  // And its TIMING is this tick's: `applyThemedCards` sets the stored figure
  // AFTER `applyBucketCards` has run, so a card built from state would be
  // built from the PREVIOUS run's aggregate every time. The run path passes
  // the payload's own value straight through and stores it here for the
  // drop-down changes that come later, which have no payload to read.
  const [bucketAggregateReturn, setBucketAggregateReturn] = useState<{
    value: number | null;
    unavailable?: string;
  }>({ value: null });
  // Which footers the three value tiles carry, which follows the scope on
  // screen -- see summaryCardFooters for why all three change together on
  // Net Value and why the mapping is not inlined here.
  const summaryFooters = useMemo(
    () => summaryValueCardFooters(selectedSummary),
    [selectedSummary],
  );
  // Whether a given Summary tile recedes on the scope now selected (task
  // 3580). ASKED PER TILE rather than once for the row, because the answer
  // differs between them: Income Received and Margin Interest do not move
  // with Net Value and recede, while the Time-Wgt Return does move with it
  // (task 3637) and stays at full strength. Every one of the three asks, so
  // the membership lives in summaryCardFooters where a test can reach it and
  // not in which of these three JSX props happens to be present.
  // Read straight rather than memoised: it is an enum comparison and a Set
  // lookup, and the boolean is what the cards below are compared on anyway.
  const tileDimmed = (tile: StrategyReportSingleValueCardLabels) =>
    summaryTileDimmed(selectedSummary, tile);
  // Income Received and Margin Interest need MORE than that predicate can
  // say (task 3639): on some scopes they withhold the FIGURE rather than only
  // receding, and each state reads its own dimming back off `tileDimmed`'s
  // mapping so the membership is still single-homed there. Both are resolved
  // once here and read by the live card and the PDF payload alike, which is
  // what keeps the paper copy from printing a figure the screen withheld.
  //
  // MEMOISED, where the predicate above is read straight: each of these is a
  // fresh object every render, and the PDF payload below depends on them --
  // an unmemoised state would rebuild that whole payload on every keystroke
  // anywhere on the page.
  //
  // Whether the reader has typed their own figure into either tile on the
  // scope now on screen (operator, task 3781). Net Value withholds both
  // tiles' figures but still takes an override, and a typed figure undims
  // the tile and prints on paper. Session-only and cleared by every
  // `applyBucketCards` -- a scope switch or a re-run -- because that call
  // overwrites the stored figure the flag vouches for, exactly as it does
  // every other override on this row.
  const [customSummaryTiles, setCustomSummaryTiles] = useState({
    income: false,
    marginInterest: false,
  });
  const incomeTile = useMemo(
    () => summaryIncomeTile(selectedSummary, customSummaryTiles.income),
    [selectedSummary, customSummaryTiles.income],
  );
  const marginInterestTile = useMemo(
    () =>
      summaryMarginInterestTile(
        selectedSummary,
        customSummaryTiles.marginInterest,
      ),
    [selectedSummary, customSummaryTiles.marginInterest],
  );
  // The two tiles' override handlers: the stored figure takes the number, and
  // the flag above says it is the reader's -- which only Net Value reads.
  // Emptying the box drops the flag and the tile goes back to its withheld
  // N/A; the stored figure is left alone, since the withheld state never
  // shows it and the next scope switch or run overwrites it anyway.
  const handleIncomeChange = useCallback(
    (value: number) => {
      setIncomeReceived(value);
      setCustomSummaryTiles((tiles) => ({ ...tiles, income: true }));
    },
    [setIncomeReceived],
  );
  const handleMarginInterestChange = useCallback(
    (value: number) => {
      setMarginInterest(value);
      setCustomSummaryTiles((tiles) => ({ ...tiles, marginInterest: true }));
    },
    [setMarginInterest],
  );
  const clearIncomeCustom = useCallback(
    () => setCustomSummaryTiles((tiles) => ({ ...tiles, income: false })),
    [],
  );
  const clearMarginInterestCustom = useCallback(
    () =>
      setCustomSummaryTiles((tiles) => ({ ...tiles, marginInterest: false })),
    [],
  );
  // What each of the two actually shows: its own stored figure, or the seed
  // or empty sentinel its state substitutes for one.
  const incomeFigure = summaryTileValue(incomeTile, incomeReceived);
  const marginInterestFigure = summaryTileValue(
    marginInterestTile,
    marginInterest,
  );
  // Bucket 1's and Bucket 3's figures are their dividends ALONE, so their
  // tiles say so rather than carrying the standing "Divs & Option Premium"
  // that names a term they do not contain (tasks 3639, 3790). Resolved here for the same reason the figure is:
  // the card and the PDF payload each render it, and they must not disagree.
  const incomeFooter =
    incomeTile.footer ??
    StrategyReportSingleValueCardDescriptions.INCOME_RECEIVED;
  // Which time-weighted return produced the figures ON SCREEN, which is what
  // the gear shows. Session-only on purpose: it is a question the reader asks
  // of THIS run, so it is not written to redux, storage or the account -- a
  // fresh mount is back on DAILY_WEIGHT_ADJUSTMENT, the default.
  //
  // Set from the backend's echo of the run that succeeded, never from the
  // pick. A run can fail -- a rejected job, a network error -- and the cards,
  // the chart and the Excel link then still hold the PREVIOUS formula's
  // results. A radio that had moved on the click would be naming a formula
  // nothing on screen was calculated by, which is the one thing a selector
  // over two near-identical figures must never do.
  const [twrFormula, setTwrFormula] = useState<BucketTwrFormula>(
    BucketTwrFormula.DAILY_WEIGHT_ADJUSTMENT,
  );
  // The option-pairing fix (task 3869), ON by default. Unlike the formula
  // above it changes what the backend CALCULATES, so it travels on the next
  // Calculate and retires the run on screen (see the reset effect below).
  const [optionPairingV2, setOptionPairingV2] = useState(true);
  const [twrFormulaAnchor, setTwrFormulaAnchor] = useState<HTMLElement | null>(
    null,
  );

  const applyBucketCards = useCallback(
    (
      totals: BucketTotals,
      summary: BucketSummaryEnum,
      // Passed rather than read from `bucketAggregateReturn` above: the run
      // path applies the cards before the state holding this has settled, so
      // the caller hands over the value it is also storing. Long Value is the
      // only scope that reads it.
      aggregateReturn: { value: number | null; unavailable?: string },
      // The gear's pick, passed rather than read from `twrFormula` above for
      // the reason `aggregateReturn` is: the pick handler applies the cards
      // in the same tick it sets the state, when this callback would still
      // close over the previous value. Every other caller hands over the
      // state value it is already holding.
      formula: BucketTwrFormula,
    ) => {
      // Long Value builds its RETURN and MARGIN cards from the buckets' own
      // here, and reads its three VALUE cards (task 3636) and its INCOME
      // (task 3574) off the backend's `long_value` entry -- longValueCards
      // says which figures the two differ on and why. Every other scope, Net
      // Value included, reads the entry it was sent throughout.
      //
      // Split out rather than folded into one expression because only this
      // scope can refuse the value cards, and the refusal has to survive the
      // widening to the shared type below: `totals[summary]` has no such
      // field, so reading it off `cards` would not narrow.
      let valuesUnavailable: string | undefined;
      // The two value tiles' date notes (operator, task 3730), resolved here
      // for the reason the refusal beside it is: only the backend's own entry
      // carries the dates, and LongValueSummaryCards has no such fields, so
      // reading them off `cards` after the widening below would not compile
      // -- and Long Value has no single date to state anyway. Net Value takes
      // the same branch as a numbered bucket and needs no test of its own:
      // the backend emits the keys for "1"/"2"/"3" alone (types.ts), so its
      // entry yields no note through the very same read.
      let dateNotes: ReturnType<typeof summaryValueDateNotes> = {};
      let cards: BucketSingleInfoCards | LongValueSummaryCards | undefined;
      if (summary === BucketSummaryEnum.LONG_VALUE) {
        const longCards = longValueCards(totals, aggregateReturn);
        if (!longCards) return;
        cards = longCards;
        valuesUnavailable = longCards.values_unavailable;
      } else {
        const bucketCards = totals[summary]?.single_info_cards;
        cards = bucketCards;
        dateNotes = summaryValueDateNotes(bucketCards);
      }
      if (!cards) return;
      setStartingValue(cards.card_1);
      setEndingValue(cards.card_2);
      setNetChange(cards.card_3);
      setValueCardsReason(valuesUnavailable);
      // Set on EVERY call, like the reason above: a scope with no dates has
      // to CLEAR the previous scope's notes, or the drop-down leaves one
      // bucket's late-start date hanging off another bucket's figure.
      setStartingValueDateNote(dateNotes.startingValue);
      setEndingValueDateNote(dateNotes.endingValue);
      // The RETURN card is the one figure on this scope the gear moves, and
      // it moves with no re-run because every formula's answer arrived with
      // the run (operator, task 3764). Long Value takes the fallback inside
      // timeWeightedReturnFigure -- longValueCards carries no per-formula
      // map, because a cross-bucket total is not a BucketTwrFormula
      // measurement and reads the same under either pick.
      const twr = timeWeightedReturnFigure(cards, formula);
      setTimeWeightedReturn(twr.value);
      setTimeWeightedReturnReason(twr.unavailableReason);
      setIncomeReceived(cards.card_5);
      setMarginInterest(cards.card_6);
      setCustomSummaryTiles({ income: false, marginInterest: false });
    },
    [
      setStartingValue,
      setEndingValue,
      setNetChange,
      setValueCardsReason,
      setStartingValueDateNote,
      setEndingValueDateNote,
      setTimeWeightedReturn,
      setIncomeReceived,
      setMarginInterest,
    ],
  );

  const handleSummaryChange = useCallback(
    (summary: BucketSummaryEnum) => {
      setSelectedSummary(summary);
      if (bucketTotals)
        applyBucketCards(
          bucketTotals,
          summary,
          bucketAggregateReturn,
          twrFormula,
        );
    },
    [bucketTotals, applyBucketCards, bucketAggregateReturn, twrFormula],
  );

  const themedCardsConfig = useMemo<ReportThemedCardConfig[]>(
    () => [
      {
        label: StrategyReportThemedCardLabels.KEEP,
        description: StrategyReportThemedCardDescriptions.KEEP,
        values: keepValuesWithHooks,
        addictional_component: null,
        headerAction: keepShares ? (
          <Select
            size="small"
            value={selectedKeepAsset}
            onChange={(event) => handleKeepAssetChange(event.target.value)}
            sx={{ bgcolor: "background.paper" }}
          >
            {[KEEP_TOTAL_OPTION, ...Object.keys(keepShares.assets)].map(
              (option) => (
                <MenuItem key={option} value={option}>
                  {option}
                </MenuItem>
              ),
            )}
          </Select>
        ) : undefined,
      },
      {
        label: StrategyReportThemedCardLabels.PROTECT,
        description: StrategyReportThemedCardDescriptions.PROTECT,
        values: protectValuesWithHooks,
        // Which asset's max_stock_drop the card (and its drawdown chart)
        // shows.
        headerAction: protectDrops ? (
          <Select
            size="small"
            value={selectedProtectAsset}
            onChange={(event) => handleProtectAssetChange(event.target.value)}
            sx={{ bgcolor: "background.paper" }}
          >
            {Object.keys(protectDrops).map((symbol) => (
              <MenuItem key={symbol} value={symbol}>
                {symbol}
              </MenuItem>
            ))}
          </Select>
        ) : undefined,
        addictional_component: {
          type: PDFReportChartType.BAR,
          title: DRAWDOWN_CHART_TITLE,
          limit: maxDropLimits.maxDrop,
          negativeLimit: maxDropLimits.negativeLimit,
          data: [
            // Each bar is dropped rather than zeroed when its row holds no
            // figure -- the same rule as the donut's slices below, and the
            // readings are taken once above so the paper and the screen
            // cannot disagree about which rows had a figure.
            ...(maxStockDropPct === null
              ? []
              : [
                  {
                    label: CHART_SERIES_STOCK,
                    value: maxStockDropPct,
                    type: PDFReportValueType.PERCENTAGE,
                  },
                ]),
            ...(oeaDropPct === null
              ? []
              : [
                  {
                    label: CHART_SERIES_OEA,
                    value: oeaDropPct,
                    type: PDFReportValueType.PERCENTAGE,
                  },
                ]),
          ],
        },
      },
      {
        label: StrategyReportThemedCardLabels.DIVERSIFY,
        description: StrategyReportThemedCardDescriptions.DIVERSIFY,
        values: diversifyValuesWithHooks,
        pdfValues: diversifyPdfValues,
        // Which Bucket 1 stock the card's two concentration rows are
        // measured for, or All Stocks for the whole of the bucket (task
        // 3508). Absent when the run produced no Bucket 1 asset: there is
        // nothing to pick between, which is what an unrendered select
        // says -- the same rule the Keep and Protect switches follow.
        headerAction: diversifyStockOptions.length ? (
          <Select
            size="small"
            value={selectedDiversifyStock}
            onChange={(event) =>
              handleDiversifyStockChange(event.target.value)
            }
            sx={{ bgcolor: "background.paper" }}
          >
            {[DIVERSIFY_ALL_STOCKS_OPTION, ...diversifyStockOptions].map(
              (option) => (
                <MenuItem key={option} value={option}>
                  {option}
                </MenuItem>
              ),
            )}
          </Select>
        ) : undefined,
        addictional_component: {
          type: PDFReportChartType.DONUT,
          title: ALLOCATION_CHART_TITLE,
          limit: FULL_ALLOCATION_PCT,
          data: [
            // Empty when there is no figure: the PDF's donut takes numbers,
            // and a 0/100 ring on paper asserts an allocation nobody
            // calculated -- the same false claim the screen's chart refuses.
            // Both slices come from the screen chart's own allocationSlices,
            // so a concentration over 100% clamps to the same ring on paper
            // as it does on screen rather than reaching a client's PDF as a
            // negative arc.
            ...(allocationPdfSlices === null
              ? []
              : [
                  {
                    label: ALLOCATION_SERIES_CONCENTRATION,
                    value: allocationPdfSlices.concentration,
                    type: PDFReportValueType.PERCENTAGE,
                  },
                  {
                    label: ALLOCATION_SERIES_DIVERSIFIED,
                    value: allocationPdfSlices.diversified,
                    type: PDFReportValueType.PERCENTAGE,
                  },
                ]),
          ],
        },
      },
      {
        label: StrategyReportThemedCardLabels.HARVEST,
        description: StrategyReportThemedCardDescriptions.HARVEST,
        values: harvestTaxBenefitsValuesWithHooks,
        addictional_component: null,
      },
    ],
    [
      keepValuesWithHooks,
      protectValuesWithHooks,
      diversifyValuesWithHooks,
      diversifyPdfValues,
      harvestTaxBenefitsValuesWithHooks,
      maxDropLimits,
      maxStockDropPct,
      oeaDropPct,
      currentConcentration,
      currentConcentrationUnavailable,
      keepShares,
      selectedKeepAsset,
      handleKeepAssetChange,
      protectDrops,
      selectedProtectAsset,
      handleProtectAssetChange,
      diversifyStockOptions,
      selectedDiversifyStock,
      handleDiversifyStockChange,
    ],
  );

  const buildPdfRequest = useCallback((): PDFReportRequest => {
    // The performance chart as it stands on screen: the lines the reader has
    // selected, aligned onto the chart's own date axis. A series the backend
    // refused is not sent -- the client's copy carries drawn lines only
    // (operator, task 4179).
    const { dates, lines } = buildOverlayChartData(chartSpecs);
    const performance_chart: PDFReportPerformanceChart = {
      dates,
      lines: lines.map(({ title, color, dash, totals }) => ({
        title,
        color,
        dash,
        totals,
      })),
    };

    const client_info: PDFReportClientInfoItem[] = [
      {
        label: StrategyReportSingleValueCardLabels.STARTING_VALUE,
        value: pdfFigureValue(startingValue, valueCardsReason),
        type: PDFReportValueType.CURRENCY,
        description: summaryFooters.startingValue,
      },
      {
        label: StrategyReportSingleValueCardLabels.ENDING_VALUE_PDF,
        value: pdfFigureValue(endingValue, valueCardsReason),
        type: PDFReportValueType.CURRENCY,
        description: summaryFooters.endingValue,
      },
      {
        label: StrategyReportSingleValueCardLabels.NET_CHANGE,
        value: pdfFigureValue(netChange, valueCardsReason),
        type: PDFReportValueType.CURRENCY,
        description: summaryFooters.netChange,
      },
      {
        label: StrategyReportSingleValueCardLabels.TIME_WEIGHTED_RETURN_PDF,
        value: pdfFigureValue(timeWeightedReturn, timeWeightedReturnReason),
        type: PDFReportValueType.PERCENTAGE,
        description:
          StrategyReportSingleValueCardDescriptions.TIME_WEIGHTED_RETURN,
      },
      {
        label: StrategyReportSingleValueCardLabels.INCOME_RECEIVED,
        // The SCREEN's figure, not the stored one (task 3639): a scope that
        // withholds this tile withholds it on paper too, where a withheld
        // figure becomes "N/A".
        value: pdfFigureValue(incomeFigure, incomeTile.unavailableReason),
        type: PDFReportValueType.CURRENCY,
        description: incomeFooter,
      },
      {
        label: StrategyReportSingleValueCardLabels.MARGIN_INTEREST,
        value: pdfFigureValue(
          marginInterestFigure,
          marginInterestTile.unavailableReason,
        ),
        type: PDFReportValueType.CURRENCY,
        description: StrategyReportSingleValueCardDescriptions.MARGIN_INTEREST,
      },
    ];

    const theme_info: PDFReportThemeInfoItem[] = themedCardsConfig.map(
      (card) => ({
        label: card.label,
        description: card.description,
        // The export's own rows where a card has them -- today only the
        // Diversify card, whose stock switch does not travel to paper.
        values: toPdfValues(card.pdfValues ?? card.values),
        addictional_component: card.addictional_component,
      }),
    );

    return {
      type: "strategy",
      settings_info: settingsInfo,
      performance_chart,
      client_info,
      theme_info,
    };
  }, [
    chartSpecs,
    themedCardsConfig,
    settingsInfo,
    summaryFooters,
    startingValue,
    endingValue,
    netChange,
    valueCardsReason,
    timeWeightedReturn,
    timeWeightedReturnReason,
    incomeFigure,
    incomeFooter,
    incomeTile,
    marginInterestFigure,
    marginInterestTile,
  ]);

  const handleDownloadPdf = useCallback(async () => {
    try {
      const response: ReportPdfResponse =
        await getReportPdf(buildPdfRequest()).unwrap();
      if (response.download_url) {
        const a = document.createElement("a");
        a.href = response.download_url;
        a.click();
        a.remove();
        return;
      }
      throw new Error("Failed to download PDF");
    } catch (error) {
      throw new Error("Failed to download PDF");
    }
  }, [getReportPdf, buildPdfRequest]);

  const openTransactionModal = useCallback(() => {
    setIsTransactionModalOpen(true);
  }, []);

  const closeTransactionModal = useCallback(() => {
    setIsTransactionModalOpen(false);
  }, []);

  // The gear no longer re-runs the report, so this takes no formula override
  // any more (operator, task 3764): picking one re-reads the run that is
  // already on screen. What the gear's pick still does is travel on the NEXT
  // Calculate -- the workbook the Excel button downloads is written under
  // one formula, and `twrFormula` is what says which.
  const handleCalculate = useCallback(
    async () => {
      const [start_date, end_date] = settingsInfo.period;

      if (!settingsInfo.orion_account_id || !start_date || !end_date) {
        setCalculationError(
          "Cannot calculate report: missing account or date range.",
        );
        return;
      }

      setPairFailures([]);
      setIsCalculating(true);
      try {
        // Buckets are CP-only: the backend runs one combined call+put report
        // per pair, so no option_type is sent. Bucket 1/2 belong to the page's
        // account; the selected Bucket 3 is passed by ID (it may belong to a
        // different account of the client) and adds one account-level sheet
        // with dynamically calculated values.
        const started = await startBucketReportJob({
          orion_account_id: settingsInfo.orion_account_id,
          start_date,
          end_date,
          buckets: [BucketNumberEnum.BUCKET_1, BucketNumberEnum.BUCKET_2],
          ...(settingsInfo.bucket_3_id
            ? { bucket_3_id: settingsInfo.bucket_3_id }
            : {}),
          federal_tax_rate: federalTaxRate,
          ...(stateTaxRate != null ? { state_tax_rate: stateTaxRate } : {}),
          twr_formula: twrFormula,
          option_pairing_v2: optionPairingV2,
        }).unwrap();
        const job = await pollReportJob(started, (key) =>
          fetchBucketJobStatus({ key }).unwrap(),
        );
        if (job.status !== ReportJobStatus.COMPLETED || !job.result) {
          setCalculationError(
            reportJobErrorMessage(
              job,
              "Failed to calculate strategy report. Please try again.",
            ),
          );
          return;
        }
        setExcelUrl(job.result.excel_url);
        // Pairs without option history still succeed (portfolio-only sheet)
        // — only real errors pop up here.
        setPairFailures(
          job.result.reports.filter(
            (report) => report.status === BucketReportStatusEnum.ERROR,
          ),
        );

        // The run's own formula, which is the one its WORKBOOK was written
        // under -- still read off the result rather than off the pick, so a
        // job served from cache under a different formula than the one just
        // asked for is named correctly and the Excel button is not
        // mislabelled by the gear above it.
        //
        // Since task 3764 the gear no longer decides which figure the cards
        // show -- every formula's figure arrived with this run and the pick
        // chooses between them -- so this is the one place the two can
        // disagree, and applyBucketCards below is handed the SAME expression
        // to keep the card showing what the gear names.
        setTwrFormula(job.result.twr_formula ?? twrFormula);
        // Same reason: name the pairing formula the run was produced by.
        setOptionPairingV2(job.result.option_pairing_v2 ?? optionPairingV2);
        const totals = job.result.bucket_totals ?? {};
        setBucketTotals(totals);
        // Long Value's card_4 comes off the LONG VALUE SCOPE'S OWN CARD
        // (operator, task 3603). The backend now measures a cross-bucket
        // return with the same engine every bucket's figure comes from, and
        // puts it where every other scope's return already sits -- so this
        // reads one source instead of lifting a figure out of the Diversify
        // card, which is a different card measuring a different thing.
        //
        // The Diversify card's `portfolio_aggregate_return` still travels in
        // the payload and is still that card's own row; it is simply no
        // longer what this tile is built from. Its sibling
        // `overall_diversified_performance` was never a candidate: task 3573
        // narrowed that one to Buckets 2 and 3, and Long Value is the three
        // buckets summed.
        //
        // Read off THIS payload rather than off the stored figure
        // applyThemedCards is about to set below -- that setter has not run
        // yet this tick, so state here still holds the previous run's.
        // Stored alongside for the drop-down changes that follow, which have
        // no payload to read.
        const longValueCards =
          totals[BucketSummaryEnum.LONG_VALUE]?.single_info_cards;
        const runAggregate = {
          value: longValueCards?.card_4 ?? null,
          unavailable: longValueCards?.card_4_unavailable ?? undefined,
        };
        setBucketAggregateReturn(runAggregate);
        // A summary with no successful data is absent from totals.
        const summaryToShow = totals[selectedSummary]
          ? selectedSummary
          : Object.values(BucketSummaryEnum).find((summary) => totals[summary]);
        if (summaryToShow) {
          setSelectedSummary(summaryToShow);
          applyBucketCards(
            totals,
            summaryToShow,
            runAggregate,
            job.result.twr_formula ?? twrFormula,
          );
        }

        applyThemedCards(job.result.themed_cards ?? {});

        const protect = job.result.themed_cards?.protect;
        const drops = protect?.max_stock_drop ?? {};
        const dropSymbols = Object.keys(drops);
        setProtectDrops(dropSymbols.length ? drops : null);
        if (dropSymbols.length) {
          // Keep the previously picked asset when the new results still
          // carry it; otherwise default to the bucket's main asset, then
          // the first one.
          const assetToShow = dropSymbols.includes(selectedProtectAsset)
            ? selectedProtectAsset
            : (protect?.main_symbol ?? dropSymbols[0]);
          setSelectedProtectAsset(assetToShow);
          applyProtectDrop(drops, assetToShow);
        }

        // Themed-cards / graph application from the old single-pair strategy
        // report — kept commented until the bucket report returns them too:
        //
        // const themedCards = data.themed_cards as
        //   | NewReportStrategyThemedCards
        //   | undefined;
        // if (themedCards && "keep" in themedCards) {
        //   setSharesStart(themedCards.keep.shares_start ?? (sharesStart as number));
        //   setSharesEnd(themedCards.keep.shares_end ?? (sharesEnd as number));
        //   setValueStart(themedCards.keep.value_start ?? (valueStart as number));
        //   setValueEnd(themedCards.keep.value_end ?? (valueEnd as number));
        //   setDividends(themedCards.keep.dividends ?? (dividends as number));
        //   setUpsideParticipation(
        //     themedCards.keep.upside_participation ?? (upsideParticipation as number),
        //   );
        //   setBasis(themedCards.keep.basis ?? (basis as number));
        //   setTaxSavings(themedCards.keep.tax_savings ?? (taxSavings as number));
        //
        //   setMaxStockDrop(
        //     themedCards.protect.max_stock_drop ?? (maxStockDrop as number),
        //   );
        //   setOeaDrop(themedCards.protect.oea_drop ?? (oeaDrop as number));
        //   setCollarPL(themedCards.protect.collar_pl ?? (collarPL as number));
        //
        //   available_margin_balance is absent from this block on purpose: the
        //   UI hard-codes that row to 0, so wiring the payload's reserved zero
        //   into it would overwrite an operator's typed figure with a number
        //   no calculation produced.
        //
        //   overall_diversified_performance is absent from this block now
        //   that it is calculated: it is applied for real above, with the
        //   refusal-carries-a-reason handling a `??` fallback cannot do.
        //
        //   setTaxLossHarvestSleeve(
        //     themedCards.diversify.tax_loss_harvest_sleeve ??
        //       (taxLossHarvestSleeve as number),
        //   );
        //   setBeginningConcentration(
        //     themedCards.diversify.beginning_concentration ??
        //       (beginningConcentration as number),
        //   );
        //   setCurrentConcentration(
        //     themedCards.diversify.current_concentration ??
        //       (currentConcentration as number),
        //   );
        //   (Holdings is no longer among these -- task 3349 wired it to the
        //   bucket payload's diversify.holdings above, so this line is gone
        //   rather than waiting.)
        //
        //   setRealizedLosses(
        //     themedCards.harvest.realized_losses ?? (realizedLosses as number),
        //   );
        //   setLossesUsed(themedCards.harvest.losses_used ?? (lossesUsed as number));
        //   setPoolRemaining(
        //     themedCards.harvest.pool_remaining ?? (poolRemaining as number),
        //   );
        //   setSharesSold(themedCards.harvest.shares_sold ?? (sharesSold as number));
        //   setSellableToday(
        //     themedCards.harvest.sellable_today ?? (sellableToday as number),
        //   );
        // }
        //
      } catch (e) {
        setCalculationError(
          extractReportErrorMessage(
            e,
            "Failed to calculate strategy report. Please try again.",
          ),
        );
      } finally {
        setIsCalculating(false);
      }
    },
    [
      settingsInfo.period,
      settingsInfo.orion_account_id,
      settingsInfo.bucket_3_id,
      stateTaxRate,
      federalTaxRate,
      startBucketReportJob,
      fetchBucketJobStatus,
      setCalculationError,
      selectedSummary,
      applyBucketCards,
      selectedProtectAsset,
      applyProtectDrop,
      applyThemedCards,
      twrFormula,
      optionPairingV2,
    ],
  );

  // Picking a formula RE-READS the run, it does not re-run it (operator,
  // task 3764). Every formula's figure and every formula's chart line
  // travel on the one response, so the pick is a render and costs no job,
  // no poll and no wait -- where it used to fire a whole report whose cache
  // key the formula was part of.
  //
  // The two that move are the Time-Wgt Return card and the bucket's chart
  // line, and they move TOGETHER: the chart's last point is 100 plus that
  // formula's own card_4 (bucket_performance_series), so switching one
  // without the other would put a chart and a card that disagree side by
  // side. Everything else on the page -- the workbook behind the Excel
  // button, the Diversify card's aggregate rows -- stays as the run
  // produced it, and the next Calculate is what moves those, since the
  // pick is still what the request sends.
  const handleTwrFormulaChange = useCallback(
    (formula: BucketTwrFormula) => {
      setTwrFormulaAnchor(null);
      setTwrFormula(formula);
      // Applied HERE with the pick handed over explicitly rather than left
      // to an effect on `twrFormula`: the state set above has not settled
      // this tick, so an effect would re-render the cards a frame later and
      // this callback would read the previous formula.
      if (bucketTotals)
        applyBucketCards(
          bucketTotals,
          selectedSummary,
          bucketAggregateReturn,
          formula,
        );
    },
    [bucketTotals, selectedSummary, bucketAggregateReturn, applyBucketCards],
  );

  const handleDownloadExcel = useCallback(() => {
    if (!excelUrl) return;
    const a = document.createElement("a");
    a.href = excelUrl;
    a.click();
    a.remove();
  }, [excelUrl]);

  // Reset job outputs when key report inputs change
  useEffect(() => {
    setExcelUrl(null);
    setPairFailures([]);
    setBucketTotals(null);
    setKeepShares(null);
    // The Keep card's figures go with the picker above, and they have to: the
    // line above takes away the only thing on the card that says WHICH stock
    // its numbers are, so figures left standing beside it are the previous
    // account's under this one's name. Back to "unrun", which is exactly what
    // is true again — no run has produced a Keep card for these inputs — and
    // the reader's own edits go with them, because they were typed against
    // the run this change of inputs has just retired.
    setKeepFiguresState("unrun");
    setKeepRowsEdited(NO_KEEP_ROWS_EDITED);
    setProtectDrops(null);
    // Available Margin Balance is hand-entered rather than calculated, but it
    // belongs here for the same reason as everything above: it is the ONE
    // hand-entered row that reaches the client-facing PDF as a figure, so a
    // number typed for the previous client would print under this one's name
    // with nothing on the page to say it was not theirs. Back to N/A instead,
    // which takes the line off the export too. A report RE-RUN for the same
    // client and period does not come through here, so a typed figure
    // survives that.
    resetAvailableMarginBalance();
    // The harvest picker is the same case as setKeepShares(null) above, and
    // every piece of its state has to go together: the stocks belong to the
    // run that produced them, so a map left standing offers the PREVIOUS
    // client's tickers, the counts beside it read as this client's, and the
    // caption dates them to a snapshot of the other account's. The two
    // figures themselves are scope-keyed by their own hook and return to
    // their seed on their own.
    setHarvestStocks(null);
    setSelectedHarvestStock("");
    setSharesSoldPending(false);
    setSellableTodayPending(false);
    setSellableTodayFromBackend(false);
    setSharesSoldReason(undefined);
    setSellableTodayReason(undefined);
    setSellableAsOf(null);
    // A refusal describes ONE RUN, and this change of inputs has just
    // retired it -- so the sentence goes with it. Left standing, the row
    // read "N/A, this run produced no Bucket 1 stock value" for inputs no
    // run has been made against yet, which is a refusal reported before
    // anything was refused; the stored figure underneath comes back
    // instead, which is what every other calculated row on this page does
    // after an input change.
    setCurrentConcentrationUnavailable(undefined);
  }, [
    settingsInfo.orion_account_id,
    settingsInfo.period?.[0],
    settingsInfo.period?.[1],
    // Which Bucket 3 the report names is a job input like any other, and it
    // is changed on its own from Settings -- so it belongs here beside the
    // account. Missing, switching Bucket 3 left the harvest picker offering
    // the PREVIOUS bucket's tickers with its counts still on the two rows and
    // the caption still dating them, which is worse than the account case it
    // resembles: Bucket 3 may belong to a different account of the client
    // (see the request builder above), so those counts can be another
    // account's entirely, and they reach the client's PDF that way.
    settingsInfo.bucket_3_id,
    // A new rate means a new job — the ready excel belongs to the old one.
    stateTaxRate,
    federalTaxRate,
    // So does a new option-pairing formula: every option figure moves.
    optionPairingV2,
  ]);

  return (
    <Card>
      <CardContent>
        <RequestNotification
          open={Boolean(calculationError)}
          title="Calculation failed"
          message={calculationError}
          onClose={() => setCalculationError("")}
        />
        <RequestNotification
          open={pairFailures.length > 0}
          severity="warning"
          title={`${pairFailures.length} bucket report(s) failed`}
          message={pairFailures
            .map(
              (failure) =>
                `B${failure.bucket}${
                  failure.symbol ? ` ${failure.symbol}` : ""
                }${
                  failure.option_type ? ` ${failure.option_type}` : ""
                }: ${failure.message}`,
            )
            .join(" · ")}
          onClose={() => setPairFailures([])}
        />
        <SettingsReport
          type={PerformanceReportTypeEnum.STRATEGY}
          header="Optic Equity Access Performance"
          subtitle="OPTIC ASSET MANAGEMENT"
        />
        <Grid container justifyContent="center" sx={{ mt: 1 }}>
          <Grid item sx={{ mr: 2 }}>
            <Button
              variant="contained"
              color="primary"
              onClick={handleDownloadPdf}
              disabled={isPdfLoading}
            >
              {isPdfLoading ? "Generating…" : "Download PDF"}
            </Button>
          </Grid>
          <Grid item sx={{ mr: 2 }}>
            <Button
              variant="contained"
              color="primary"
              onClick={handleDownloadExcel}
              disabled={!excelUrl}
            >
              {excelUrl ? "Download Excel" : "Download Excel"}
            </Button>
          </Grid>
          <Grid item sx={{ mr: 2 }}>
            <Button
              variant="contained"
              color="primary"
              onClick={() => handleCalculate()}
              disabled={isPdfLoading || isCalculating}
            >
              {isCalculating ? "Calculating…" : `Calculate`}
            </Button>
          </Grid>
          <Grid item sx={{ mr: 2 }}>
            <Button
              variant="contained"
              color="primary"
              onClick={openTransactionModal}
              disabled={isPdfLoading}
            >
              {isPdfLoading ? "Updating…" : "Transactions"}
            </Button>
          </Grid>
        </Grid>
        <ChartsReport
          bucketTotals={bucketTotals}
          period={settingsInfo.period}
          twrFormula={twrFormula}
          onSpecsChange={setChartSpecs}
        />
        <Grid
          container
          justifyContent="space-between"
          alignItems="center"
          sx={{ mb: 1 }}
        >
          <Typography variant="h6">Summary</Typography>
          <Grid item>
            <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
              <IconButton
                size="small"
                aria-label="Time-weighted return formula"
                sx={{ fontSize: 20 }}
                disabled={isCalculating}
                onClick={(event) => setTwrFormulaAnchor(event.currentTarget)}
              >
                <SettingsIcon fontSize="inherit" />
              </IconButton>
              <Popover
                open={Boolean(twrFormulaAnchor)}
                anchorEl={twrFormulaAnchor}
                onClose={() => setTwrFormulaAnchor(null)}
                anchorOrigin={{ vertical: "bottom", horizontal: "left" }}
              >
                <Box sx={{ p: 2, width: 320 }}>
                  <FormControl>
                    <FormLabel
                      id="twr-formula-label"
                      sx={{ mb: 1, typography: "subtitle2" }}
                    >
                      Time-Weighted Return
                    </FormLabel>
                    <RadioGroup
                      aria-labelledby="twr-formula-label"
                      value={twrFormula}
                      onChange={(event) =>
                        handleTwrFormulaChange(
                          event.target.value as BucketTwrFormula,
                        )
                      }
                    >
                      {Object.values(BucketTwrFormula).map((formula) => (
                        <FormControlLabel
                          key={formula}
                          value={formula}
                          control={<Radio size="small" />}
                          label={twrFormulaLabel(
                            formula,
                            settingsInfo.period[0] || undefined,
                          )}
                          sx={{
                            mr: 0,
                            "& .MuiFormControlLabel-label": {
                              typography: "body2",
                            },
                          }}
                        />
                      ))}
                    </RadioGroup>
                  </FormControl>
                  <FormControl sx={{ mt: 2, display: "block" }}>
                    <FormLabel
                      id="option-pairing-label"
                      sx={{
                        mb: 0.5,
                        typography: "subtitle2",
                        display: "block",
                      }}
                    >
                      Option Pairing
                    </FormLabel>
                    <FormControlLabel
                      control={
                        <Switch
                          size="small"
                          checked={optionPairingV2}
                          onChange={(event) =>
                            setOptionPairingV2(event.target.checked)
                          }
                          inputProps={{
                            "aria-labelledby": "option-pairing-label",
                          }}
                        />
                      }
                      label={
                        optionPairingV2
                          ? "Fixed pairing (PUT expiry)"
                          : "Previous formula"
                      }
                      sx={{
                        mr: 0,
                        "& .MuiFormControlLabel-label": {
                          typography: "body2",
                        },
                      }}
                    />
                    <Typography
                      variant="caption"
                      color="text.secondary"
                      display="block"
                    >
                      Applies on the next Calculate.
                    </Typography>
                  </FormControl>
                </Box>
              </Popover>
              <BucketSummarySelect
                value={selectedSummary}
                bucketTotals={bucketTotals ?? undefined}
                onChange={handleSummaryChange}
              />
            </Box>
          </Grid>
        </Grid>
        <Grid container spacing={4} columns={{ lg: 12, md: 6, xs: 12 }}>
          <Grid item lg={4} xs={12} md={6}>
            <SingleValueCard
              value={startingValue}
              label={StrategyReportSingleValueCardLabels.STARTING_VALUE}
              description={summaryFooters.startingValue}
              type={SingleValueCardType.CURRENCY}
              color={SingleValueCardColor.PRIMARY}
              onValueChange={setStartingValue}
              unavailableReason={valueCardsReason}
              note={startingValueDateNote}
            />
          </Grid>
          <Grid item lg={4} xs={12} md={6}>
            <SingleValueCard
              value={endingValue}
              label={StrategyReportSingleValueCardLabels.ENDING_VALUE_CARD}
              description={summaryFooters.endingValue}
              type={SingleValueCardType.CURRENCY}
              color={SingleValueCardColor.PRIMARY}
              onValueChange={setEndingValue}
              unavailableReason={valueCardsReason}
              note={endingValueDateNote}
            />
          </Grid>
          <Grid item lg={4} xs={12} md={6}>
            <SingleValueCard
              value={netChange}
              label={StrategyReportSingleValueCardLabels.NET_CHANGE}
              description={summaryFooters.netChange}
              type={SingleValueCardType.CURRENCY}
              color={SingleValueCardColor.PRIMARY}
              onValueChange={setNetChange}
              unavailableReason={valueCardsReason}
            />
          </Grid>
          <Grid item lg={4} xs={12} md={6}>
            <SingleValueCard
              value={timeWeightedReturn}
              label={StrategyReportSingleValueCardLabels.TIME_WEIGHTED_RETURN_CARD}
              description={
                StrategyReportSingleValueCardDescriptions.TIME_WEIGHTED_RETURN
              }
              type={SingleValueCardType.PERCENTAGE}
              color={SingleValueCardColor.PRIMARY}
              onValueChange={setTimeWeightedReturn}
              dimmed={tileDimmed(
                StrategyReportSingleValueCardLabels.TIME_WEIGHTED_RETURN_CARD,
              )}
              unavailableReason={timeWeightedReturnReason}
            />
          </Grid>
          <Grid item lg={4} xs={12} md={6}>
            <SingleValueCard
              value={incomeFigure}
              label={StrategyReportSingleValueCardLabels.INCOME_RECEIVED}
              description={incomeFooter}
              type={SingleValueCardType.CURRENCY}
              color={SingleValueCardColor.PRIMARY}
              onValueChange={handleIncomeChange}
              onClear={
                incomeTile.overridable ? clearIncomeCustom : undefined
              }
              dimmed={incomeTile.dimmed}
              overridable={incomeTile.overridable}
              unavailableReason={incomeTile.unavailableReason}
            />
          </Grid>
          <Grid item lg={4} xs={12} md={6}>
            <SingleValueCard
              value={marginInterestFigure}
              label={StrategyReportSingleValueCardLabels.MARGIN_INTEREST}
              description={
                StrategyReportSingleValueCardDescriptions.MARGIN_INTEREST
              }
              type={SingleValueCardType.CURRENCY}
              color={SingleValueCardColor.ERROR}
              onValueChange={handleMarginInterestChange}
              onClear={
                marginInterestTile.overridable
                  ? clearMarginInterestCustom
                  : undefined
              }
              dimmed={marginInterestTile.dimmed}
              overridable={marginInterestTile.overridable}
              unavailableReason={marginInterestTile.unavailableReason}
            />
          </Grid>
        </Grid>
        <Grid container spacing={4} columns={{ lg: 12, md: 6, xs: 12 }}>
          {themedCardsConfig.map((card, index) => (
            <Grid item lg={6} xs={12} md={6} key={card.label}>
              <MultiValuesCard
                title={card.label}
                values={card.values}
                headerAction={card.headerAction}
                description={
                  index === 0
                    ? StrategyReportThemedCardBlurbs.KEEP
                    : index === 1
                      ? StrategyReportThemedCardBlurbs.PROTECT
                      : index === 2
                        ? StrategyReportThemedCardBlurbs.DIVERSIFY
                        : StrategyReportThemedCardBlurbs.HARVEST
                }
                additionalComponents={
                  index === 1 ? (
                    <DrawdownComparisonChart
                      values={protectValuesWithHooks}
                      maxDrop={maxDropLimits.maxDrop}
                      negativeLimit={maxDropLimits.negativeLimit}
                      onLimitsChange={setMaxDropLimits}
                    />
                  ) : index === 2 ? (
                    <CurrentAllocationChartReport
                      value={currentConcentrationPct}
                      unavailableReason={currentConcentrationUnavailable}
                    />
                  ) : null
                }
              />
            </Grid>
          ))}
        </Grid>
        <TransactionModal
          open={isTransactionModalOpen}
          onClose={closeTransactionModal}
          settingsInfo={settingsInfo}
          reportType={PerformanceReportTypeEnum.STRATEGY}
          onDeselectedTransactionsChange={setDeselectedTransactions}
        />
      </CardContent>
    </Card>
  );
}

export const StrategyReport = () => (
  <ReportSettingsProvider>
    <StrategyReportContent />
  </ReportSettingsProvider>
);

export default StrategyReport;
